import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Plus, Check, X, AlertCircle, User, ChevronLeft, ChevronRight, Timer, Users, BarChart3, Briefcase, RotateCcw, Eye, Shield, CheckCircle, XCircle, Hourglass, DollarSign, Settings, Save, Edit, Info, Zap, Palmtree, HeartPulse, Home, Cloud, Sparkles, MoreHorizontal, GraduationCap, Phone, Sunrise, Sunset, Moon, Sun, ArrowRight, CalendarDays, TrendingUp, Loader2, Repeat, Paperclip, Gift, PartyPopper } from 'lucide-react';
import { getUkHoliday, HOLIDAY_TONE_STYLES, holidaysInMonth } from '../../utils/ukHolidays';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField, JewelChipRow } from '../../components/ui/jewel-modal';
import { toast } from 'sonner';
import axios from 'axios';
import OvertimeMarketplace from '../../components/staff/OvertimeMarketplace';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEAVE_TYPES = [
  { id: 'annual',      label: 'Annual Leave',   short: 'Annual',      icon: Palmtree,   accent: 'cyan',    grad: 'from-cyan-500 to-sky-500' },
  { id: 'sick',        label: 'Sick Leave',     short: 'Sick',        icon: HeartPulse, accent: 'rose',    grad: 'from-rose-500 to-red-500' },
  { id: 'personal',    label: 'Personal',       short: 'Personal',    icon: Home,       accent: 'violet',  grad: 'from-violet-500 to-fuchsia-500' },
  { id: 'bereavement', label: 'Bereavement',    short: 'Family',      icon: Cloud,      accent: 'slate',   grad: 'from-slate-500 to-slate-400' },
  { id: 'other',       label: 'Other',          short: 'Other',       icon: Sparkles,   accent: 'amber',   grad: 'from-amber-500 to-orange-500' },
];

const SHIFT_TYPES = [
  { id: 'regular',  label: 'Regular Shift', short: 'Regular',  icon: Briefcase,      accent: 'emerald', grad: 'from-emerald-500 to-green-500' },
  { id: 'overtime', label: 'Overtime',      short: 'Overtime', icon: Zap,            accent: 'cyan',    grad: 'from-cyan-500 to-sky-500' },
  { id: 'training', label: 'Training',      short: 'Training', icon: GraduationCap,  accent: 'violet',  grad: 'from-violet-500 to-fuchsia-500' },
  { id: 'on-call',  label: 'On-Call',       short: 'On-Call',  icon: Phone,          accent: 'amber',   grad: 'from-amber-500 to-orange-500' },
];

const SHIFT_PRESETS = [
  { id: 'morning',   label: 'Morning',   start: '09:00', end: '13:00', icon: Sunrise },
  { id: 'afternoon', label: 'Afternoon', start: '13:00', end: '17:00', icon: Sun },
  { id: 'evening',   label: 'Evening',   start: '17:00', end: '22:00', icon: Sunset },
  { id: 'night',     label: 'Night',     start: '22:00', end: '06:00', icon: Moon },
  { id: 'fullday',   label: 'Full Day',  start: '09:00', end: '17:00', icon: Clock },
];

const LEAVE_PRESETS = [
  { id: 'today',    label: 'Today',        days: 0 },
  { id: 'tomorrow', label: 'Tomorrow',     days: 1 },
  { id: '3days',    label: '3 Days',       days: 3 },
  { id: 'week',     label: '1 Week',       days: 7 },
  { id: 'twoweeks', label: '2 Weeks',      days: 14 },
];

const DAYS_OF_WEEK = [
  { id: 0, name: 'Monday', short: 'Mon' },
  { id: 1, name: 'Tuesday', short: 'Tue' },
  { id: 2, name: 'Wednesday', short: 'Wed' },
  { id: 3, name: 'Thursday', short: 'Thu' },
  { id: 4, name: 'Friday', short: 'Fri' },
  { id: 5, name: 'Saturday', short: 'Sat' },
  { id: 6, name: 'Sunday', short: 'Sun' }
];

const PAYDAY_TYPES = [
  { id: 'weekly', name: 'Weekly' },
  { id: 'biweekly', name: 'Bi-Weekly' },
  { id: 'monthly', name: 'Monthly' }
];

const LeaveManagementPage = ({ getAuthHeaders, currentUser }) => {
  const [myRequests, setMyRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [pendingShifts, setPendingShifts] = useState([]);
  const [shiftStats, setShiftStats] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [showShiftApprovalModal, setShowShiftApprovalModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clockData, setClockData] = useState({ records: [], total_time: '0h 0m' });
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [calendarDateFilter, setCalendarDateFilter] = useState('all'); // all | this-week | next-week | pending-only
  
  // Leave allowance state
  const [myAllowance, setMyAllowance] = useState(null);
  const [allLeaveConfigs, setAllLeaveConfigs] = useState([]);
  
  // Payday state
  const [paydayCalendar, setPaydayCalendar] = useState([]);
  const [paydayConfigs, setPaydayConfigs] = useState([]);
  
  // Regular schedules state
  const [allSchedules, setAllSchedules] = useState([]);
  
  // Workforce settings modal
  const [showWorkforceModal, setShowWorkforceModal] = useState(false);
  const [workforceTab, setWorkforceTab] = useState('schedules');
  const [selectedStaffConfig, setSelectedStaffConfig] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configType, setConfigType] = useState(''); // 'schedule', 'payday', 'leave'

  // Heatmap state — managers/execs get a density overlay on the calendar
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [densityData, setDensityData] = useState({ days: [], workforce_size: 0 });
  
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    leave_type: 'annual',
    reason: ''
  });
  
  const [shiftFormData, setShiftFormData] = useState({
    staff_username: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    shift_type: 'regular',
    notes: ''
  });
  
  const [reviewData, setReviewData] = useState({
    status: '',
    review_notes: ''
  });

  const [shiftApprovalData, setShiftApprovalData] = useState({
    status: '',
    approval_notes: ''
  });
  
  // Config forms
  const [scheduleForm, setScheduleForm] = useState({
    staff_username: '',
    day_of_week: 0,
    start_time: '09:00',
    end_time: '17:00',
    is_active: true
  });
  
  const [paydayForm, setPaydayForm] = useState({
    staff_username: '',
    payday_type: 'monthly',
    payday_day: 1
  });
  
  const [leaveConfigForm, setLeaveConfigForm] = useState({
    staff_username: '',
    minimum_hours: 50,
    total_allowance: 160,
    extra_hours: 0
  });
  
  // Add hours modal state
  const [showAddHoursModal, setShowAddHoursModal] = useState(false);
  const [addHoursForm, setAddHoursForm] = useState({
    staff_username: '',
    hours: 0,
    reason: ''
  });
  
  // 12 month view state
  const [shiftViewMonths, setShiftViewMonths] = useState(1);
  const [expandedRequest, setExpandedRequest] = useState(null);

  // My Shifts (own upcoming + past) — visible to all staff
  const [myShifts, setMyShifts] = useState({ upcoming: [], past: [], summary: null });
  const [myShiftsView, setMyShiftsView] = useState('upcoming'); // 'upcoming' | 'past'

  // New features state
  const [weeklyRoster, setWeeklyRoster] = useState(null);
  const [rosterWeek, setRosterWeek] = useState('');
  const [staffAvailability, setStaffAvailability] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [teamCalendar, setTeamCalendar] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', start_time: '09:00', end_time: '17:00', shift_type: 'regular', description: '' });

  // Role checks
  const isManager = currentUser?.role === 'manager';
  const isExecutive = ['superadmin', 'executive', 'admin'].includes(currentUser?.role);
  const canApproveLeave = isManager || isExecutive;
  const canApproveShifts = isExecutive;
  const canAddShifts = isManager || isExecutive;
  const canViewAllShifts = isManager || isExecutive;

  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  useEffect(() => {
    if (canViewAllShifts && viewMode === 'shifts') {
      fetchShiftStats();
      if (isExecutive) {
        fetchPendingShifts();
      }
    }
  }, [viewMode, canViewAllShifts, isExecutive]);

  const fetchData = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const [myRes, calRes, shiftsRes, clockRes, allowanceRes, paydayCalRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/leave/my-requests`, { headers }),
        axios.get(`${BACKEND_URL}/api/leave/calendar`, { headers }),
        axios.get(`${BACKEND_URL}/api/shifts/calendar?month=${currentMonth.getMonth() + 1}&year=${currentMonth.getFullYear()}`, { headers }),
        axios.get(`${BACKEND_URL}/api/clock/history?days=30`, { headers }).catch(() => ({ data: { records: [], total_time: '0h 0m' } })),
        axios.get(`${BACKEND_URL}/api/leave/my-allowance`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/leave/payday-calendar?month=${currentMonth.getMonth() + 1}&year=${currentMonth.getFullYear()}`, { headers }).catch(() => ({ data: [] }))
      ]);
      setMyRequests(myRes.data);
      setCalendarData(calRes.data);
      setShifts(shiftsRes.data);
      setClockData(clockRes.data);
      setMyAllowance(allowanceRes.data);
      setPaydayCalendar(paydayCalRes.data);

      if (canApproveLeave) {
        const [allRes, staffRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/leave/all-requests`, { headers }),
          axios.get(`${BACKEND_URL}/api/shifts/staff-list`, { headers }).catch(() => ({ data: [] }))
        ]);
        setAllRequests(allRes.data);
        setStaffList(staffRes.data);
      }
      
      if (isExecutive) {
        const [leaveConfigsRes, paydayConfigsRes, schedulesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/leave/leave-configs`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/leave/payday-all`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/leave/schedules`, { headers }).catch(() => ({ data: [] }))
        ]);
        setAllLeaveConfigs(leaveConfigsRes.data);
        setPaydayConfigs(paydayConfigsRes.data);
        setAllSchedules(schedulesRes.data);
      }

      // Fetch new features data
      try {
        const [templatesRes, swapsRes, historyRes, teamCalRes, myShiftsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/shifts/templates`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/shifts/swap-requests`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/leave/history-timeline`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/leave/team-calendar?month=${currentMonth.getFullYear()}-${String(currentMonth.getMonth()+1).padStart(2,'0')}`, { headers }).catch(() => ({ data: { leaves: [] } })),
          axios.get(`${BACKEND_URL}/api/shifts/my-shifts?days_ahead=90&days_back=30`, { headers }).catch(() => ({ data: { upcoming: [], past: [], summary: null } }))
        ]);
        setShiftTemplates(templatesRes.data || []);
        setSwapRequests(swapsRes.data || []);
        setLeaveHistory(historyRes.data || []);
        setTeamCalendar(teamCalRes.data?.leaves || []);
        setMyShifts(myShiftsRes.data || { upcoming: [], past: [], summary: null });
      } catch {}

      if (canViewAllShifts) {
        try {
          const monthParam = currentMonth.getMonth() + 1;
          const yearParam = currentMonth.getFullYear();
          const [rosterRes, availRes, hoursRes, densityRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/shifts/weekly-roster`, { headers }).catch(() => ({ data: null })),
            axios.get(`${BACKEND_URL}/api/shifts/staff-availability`, { headers }).catch(() => ({ data: [] })),
            axios.get(`${BACKEND_URL}/api/shifts/weekly-hours`, { headers }).catch(() => ({ data: [] })),
            axios.get(`${BACKEND_URL}/api/shifts/density?year=${yearParam}&month=${monthParam}`, { headers }).catch(() => ({ data: { days: [], workforce_size: 0 } })),
          ]);
          setWeeklyRoster(rosterRes.data);
          setStaffAvailability(availRes.data || []);
          setWeeklyHours(hoursRes.data || []);
          setDensityData(densityRes.data || { days: [], workforce_size: 0 });
        } catch {}
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchShiftStats = async () => {
    try {
      const headers = getAuthHeaders();
      const now = new Date();
      const startOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const endDate = `${endOfMonth.getFullYear()}-${String(endOfMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfMonth.getDate()).padStart(2, '0')}`;
      
      const response = await axios.get(`${BACKEND_URL}/api/shifts/stats?start_date=${startOfMonth}&end_date=${endDate}`, { headers });
      setShiftStats(response.data);
    } catch (error) {
      console.error('Error fetching shift stats:', error);
    }
  };

  const fetchPendingShifts = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${BACKEND_URL}/api/shifts/pending`, { headers });
      setPendingShifts(response.data);
    } catch (error) {
      console.error('Error fetching pending shifts:', error);
    }
  };

  // Day click handler - shows modal with day details
  const handleDayClick = async (date) => {
    if (!date) return;
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    const { leaves, shifts: dayShifts, paydays, clockRecords, regularShifts } = getEventsForDate(date);
    const holiday = getUkHoliday(date);
    
    setSelectedDayData({
      date: dateStr,
      displayDate: date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
      leaves,
      shifts: dayShifts,
      paydays,
      clockRecords,
      regularShifts,
      holiday,
    });
    setShowDayModal(true);
  };

  const handleSubmitRequest = async () => {
    if (!formData.start_date || !formData.end_date || !formData.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      toast.error('End date must be after start date');
      return;
    }
    
    // Check leave allowance
    if (myAllowance && myAllowance.remaining_hours <= 0) {
      toast.error('You have no remaining leave hours');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/leave/requests`, formData, { headers });
      toast.success('Leave request submitted successfully');
      setShowRequestModal(false);
      setFormData({ start_date: '', end_date: '', leave_type: 'annual', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleWithdrawRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to withdraw this leave request?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/leave/requests/${requestId}/withdraw`, {}, { headers });
      toast.success('Leave request withdrawn successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw request');
    }
  };

  const handleSubmitShift = async () => {
    if (!shiftFormData.staff_username || !shiftFormData.date || !shiftFormData.start_time || !shiftFormData.end_time) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${BACKEND_URL}/api/shifts/`, shiftFormData, { headers });
      
      if (response.data.status === 'pending') {
        toast.success('Shift submitted for executive approval');
      } else {
        toast.success('Shift added successfully');
      }
      
      setShowShiftModal(false);
      setShiftFormData({ staff_username: '', date: '', start_time: '09:00', end_time: '17:00', shift_type: 'regular', notes: '' });
      fetchData();
      if (viewMode === 'shifts') {
        fetchShiftStats();
        if (isExecutive) fetchPendingShifts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add shift');
    }
  };

  const handleApproveShift = async (status) => {
    if (!selectedShift) return;

    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/shifts/${selectedShift.id}/approve`, {
        status,
        approval_notes: shiftApprovalData.approval_notes
      }, { headers });
      toast.success(`Shift ${status}`);
      setShowShiftApprovalModal(false);
      setSelectedShift(null);
      setShiftApprovalData({ status: '', approval_notes: '' });
      fetchData();
      fetchShiftStats();
      fetchPendingShifts();
    } catch (error) {
      toast.error('Failed to process shift approval');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/shifts/${shiftId}`, { headers });
      toast.success('Shift deleted');
      fetchData();
      if (viewMode === 'shifts') fetchShiftStats();
    } catch (error) {
      toast.error('Failed to delete shift');
    }
  };

  const handleReview = async (status) => {
    if (!selectedRequest) return;

    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/leave/requests/${selectedRequest.id}/review`, {
        status,
        review_notes: reviewData.review_notes
      }, { headers });
      toast.success(`Leave request ${status}`);
      setShowReviewModal(false);
      setSelectedRequest(null);
      setReviewData({ status: '', review_notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to review request');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/leave/requests/${requestId}`, { headers });
      toast.success('Request deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };
  
  // Workforce settings handlers
  const handleSaveSchedule = async () => {
    if (!scheduleForm.staff_username) {
      toast.error('Please select a staff member');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/leave/schedules`, scheduleForm, { headers });
      toast.success('Schedule saved');
      setShowConfigModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    }
  };

  const handleSavePayday = async () => {
    if (!paydayForm.staff_username) {
      toast.error('Please select a staff member');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/leave/payday`, paydayForm, { headers });
      toast.success('Payday configured');
      setShowConfigModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payday');
    }
  };

  const handleSaveLeaveConfig = async () => {
    if (!leaveConfigForm.staff_username) {
      toast.error('Please select a staff member');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/leave/leave-config`, leaveConfigForm, { headers });
      toast.success('Leave allowance configured');
      setShowConfigModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save leave config');
    }
  };

  const handleAddHours = async () => {
    if (!addHoursForm.staff_username || !addHoursForm.reason) {
      toast.error('Please select staff and provide a reason');
      return;
    }
    if (addHoursForm.hours === 0) {
      toast.error('Hours cannot be zero');
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.put(
        `${BACKEND_URL}/api/leave/allowance/${addHoursForm.staff_username}`,
        { hours: addHoursForm.hours, reason: addHoursForm.reason },
        { headers }
      );
      toast.success(`${addHoursForm.hours > 0 ? 'Added' : 'Deducted'} ${Math.abs(addHoursForm.hours)}h successfully`);
      setShowAddHoursModal(false);
      setAddHoursForm({ staff_username: '', hours: 0, reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust hours');
    }
  };

  const openAddHoursModal = (staff = null) => {
    setAddHoursForm({
      staff_username: staff?.username || '',
      hours: 0,
      reason: ''
    });
    setShowAddHoursModal(true);
  };
  
  const handleDeleteSchedule = async (staffUsername, dayOfWeek) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/leave/schedules/${staffUsername}/${dayOfWeek}`, { headers });
      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };
  
  const openConfigModal = (type, staff = null) => {
    setConfigType(type);
    setSelectedStaffConfig(staff);
    
    if (type === 'schedule') {
      setScheduleForm({
        staff_username: staff?.username || '',
        day_of_week: 0,
        start_time: '09:00',
        end_time: '17:00',
        is_active: true
      });
    } else if (type === 'payday') {
      const existing = paydayConfigs.find(p => p.username === staff?.username);
      setPaydayForm({
        staff_username: staff?.username || '',
        payday_type: existing?.payday_type || 'monthly',
        payday_day: existing?.payday_day || 1
      });
    } else if (type === 'leave') {
      const existing = allLeaveConfigs.find(c => c.username === staff?.username);
      setLeaveConfigForm({
        staff_username: staff?.username || '',
        minimum_hours: existing?.minimum_hours_required || 50,
        total_allowance: existing?.total_allowance || 160,
        extra_hours: existing?.extra_hours || 0
      });
    }
    
    setShowConfigModal(true);
  };

  const openReviewModal = (request) => {
    setSelectedRequest(request);
    setReviewData({ status: '', review_notes: '' });
    setShowReviewModal(true);
  };

  const openShiftApprovalModal = (shift) => {
    setSelectedShift(shift);
    setShiftApprovalData({ status: '', approval_notes: '' });
    setShowShiftApprovalModal(true);
  };

  const openStatusModal = (request) => {
    setSelectedRequest(request);
    setShowStatusModal(true);
  };

  const getLeaveTypeInfo = (typeId) => LEAVE_TYPES.find(t => t.id === typeId) || LEAVE_TYPES[4];
  const getShiftTypeInfo = (typeId) => SHIFT_TYPES.find(t => t.id === typeId) || SHIFT_TYPES[0];

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      withdrawn: 'bg-white/[0.04] text-white/50 border-white/[0.08]'
    };
    return styles[status] || styles.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'withdrawn': return <RotateCcw className="w-4 h-4 text-white/50" />;
      default: return <Hourglass className="w-4 h-4 text-cyan-400" />;
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return { leaves: [], shifts: [], paydays: [], clockRecords: [], regularShifts: [] };
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    const leaves = calendarData.filter(event => dateStr >= event.start_date && dateStr <= event.end_date);
    const dayShifts = shifts.filter(s => s.date === dateStr);
    const paydays = paydayCalendar.filter(p => p.date === dateStr);
    const clockRecords = clockData.records?.filter(r => r.date === dateStr) || [];
    
    // Get regular shifts for this day of week (0=Monday in our system, but JS uses 0=Sunday)
    const jsDayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.
    const ourDayOfWeek = jsDayOfWeek === 0 ? 6 : jsDayOfWeek - 1; // Convert to 0=Monday, 6=Sunday
    
    const regularShifts = allSchedules.filter(staff => 
      staff.schedules && staff.schedules.some(s => s.day_of_week === ourDayOfWeek && s.is_active)
    ).map(staff => {
      const schedule = staff.schedules.find(s => s.day_of_week === ourDayOfWeek && s.is_active);
      return {
        display_name: staff.display_name,
        username: staff.username,
        start_time: schedule?.start_time,
        end_time: schedule?.end_time,
        is_regular: true
      };
    });
    
    return { leaves, shifts: dayShifts, paydays, clockRecords, regularShifts };
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const days = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  // Heatmap helpers
  const densityByDate = React.useMemo(() => {
    const map = {};
    (densityData.days || []).forEach(d => { map[d.date] = d; });
    return map;
  }, [densityData]);

  const HEATMAP_STYLES = {
    empty:    { ring: 'ring-white/[0.08]',     tint: 'bg-white/[0.02]',  dot: 'bg-white/40',   label: 'Empty',        text: 'text-white/50' },
    critical: { ring: 'ring-rose-500/50',      tint: 'bg-rose-500/[0.14]',   dot: 'bg-rose-400',    label: 'Understaffed', text: 'text-rose-300' },
    low:      { ring: 'ring-amber-500/40',     tint: 'bg-amber-500/[0.10]',  dot: 'bg-amber-400',   label: 'Below target', text: 'text-amber-300' },
    optimal:  { ring: 'ring-emerald-500/40',   tint: 'bg-emerald-500/[0.10]',dot: 'bg-emerald-400', label: 'Optimal',      text: 'text-emerald-300' },
    over:     { ring: 'ring-cyan-500/40',      tint: 'bg-cyan-500/[0.10]',   dot: 'bg-cyan-400',    label: 'Overstaffed',  text: 'text-cyan-300' },
  };
  const getDensity = (date) => {
    if (!date) return null;
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return densityByDate[iso] || null;
  };

  const filteredAllRequests = statusFilter === 'all' ? allRequests : allRequests.filter(r => r.status === statusFilter);
  const pendingLeaveCount = allRequests.filter(r => r.status === 'pending').length;
  const pendingShiftCount = pendingShifts.length;

  return (
    <div className="space-y-5" data-testid="leave-management-page">
      {/* ═══ PREMIUM HERO ═══ */}
      <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0a0a14] via-[#0d0b1f] to-[#0a0a14] p-6 md:p-7">
        {/* Ambient glows */}
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] bg-cyan-500/[0.08] rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-violet-500/[0.06] rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />

        <div className="relative">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
            {/* Title block */}
            <div className="flex items-start gap-4">
              <div className="relative w-14 h-14 flex-shrink-0">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/30" />
                <div className="absolute inset-0 rounded-2xl flex items-center justify-center">
                  <CalendarDays className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-[#0a0a14] shadow-[0_0_8px_rgba(52,211,153,0.7)] animate-pulse" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-1">Time · Shifts · Balance</p>
                <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight">
                  Leave & <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">Shifts</span>
                </h1>
                <p className="text-white/50 text-sm mt-1">Command centre for time off, schedules and workforce hours</p>
              </div>
            </div>

            {/* Action bar */}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {canApproveLeave && pendingLeaveCount > 0 && (
                <Badge className="bg-violet-500/15 text-violet-300 border border-violet-500/40 px-3 py-1 animate-pulse text-[11px] tracking-wide">
                  <Hourglass className="w-3 h-3 mr-1" />{pendingLeaveCount} leave pending
                </Badge>
              )}
              {isExecutive && pendingShiftCount > 0 && (
                <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/40 px-3 py-1 animate-pulse text-[11px] tracking-wide">
                  <Timer className="w-3 h-3 mr-1" />{pendingShiftCount} shift{pendingShiftCount > 1 ? 's' : ''} pending
                </Badge>
              )}
              {isExecutive && (
                <Button onClick={() => setShowWorkforceModal(true)} size="sm" data-testid="workforce-settings-btn"
                  className="bg-white/[0.04] border border-white/10 text-white/70 hover:bg-white/[0.08] hover:text-white hover:border-white/20 h-9 text-xs gap-1.5 rounded-xl transition-all">
                  <Settings className="w-3.5 h-3.5" /> Settings
                </Button>
              )}
              {canAddShifts && (
                <Button onClick={() => setShowShiftModal(true)} size="sm" data-testid="add-shift-btn"
                  className="group bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-500/50 hover:text-emerald-200 h-9 text-xs gap-1.5 rounded-xl transition-all shadow-[0_0_0_0_rgba(52,211,153,0)] hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]">
                  <Timer className="w-3.5 h-3.5 group-hover:rotate-12 transition-transform" />
                  {isManager && !isExecutive ? 'Request Shift' : 'Add Shift'}
                </Button>
              )}
              <Button onClick={() => setShowRequestModal(true)} size="sm" data-testid="request-leave-btn"
                className="group relative bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 text-white h-9 text-xs gap-1.5 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:-translate-y-0.5 transition-all rounded-xl overflow-hidden">
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Plus className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform" /> Request Leave
              </Button>
            </div>
          </div>

          {/* Allowance strip integrated into hero */}
          {myAllowance && (
            <div className="mt-6 pt-6 border-t border-white/[0.06]">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Circular gauge */}
                <div className="md:col-span-1 flex items-center gap-4">
                  {(() => {
                    const total = (myAllowance.total_hours || 0) + (myAllowance.extra_hours || 0);
                    const used = myAllowance.used_hours || 0;
                    const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
                    const remaining = total - used;
                    const circumference = 2 * Math.PI * 34;
                    const dashOffset = circumference - (pct / 100) * circumference;
                    return (
                      <div className="relative w-20 h-20 flex-shrink-0">
                        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                          <circle cx="40" cy="40" r="34" stroke="rgba(148,163,184,0.12)" strokeWidth="6" fill="none" />
                          <circle
                            cx="40" cy="40" r="34" fill="none"
                            stroke="url(#allowanceGradient)"
                            strokeWidth="6"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={dashOffset}
                            className="transition-all duration-700"
                          />
                          <defs>
                            <linearGradient id="allowanceGradient" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%" stopColor="#22d3ee" />
                              <stop offset="100%" stopColor="#a855f7" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-lg font-bold text-white leading-none">{Math.round(pct)}<span className="text-[10px] text-white/50 ml-0.5">%</span></span>
                          <span className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">used</span>
                        </div>
                      </div>
                    );
                  })()}
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-cyan-300/70">Annual · {myAllowance.year}</p>
                    <p className="text-white font-semibold text-sm mt-0.5">Leave Balance</p>
                    <p className="text-white/40 text-[11px]">{myAllowance.remaining_hours}h remaining</p>
                  </div>
                </div>
                {/* Metrics */}
                {[
                  { label: 'Remaining', value: `${myAllowance.remaining_hours}h`, tone: 'cyan',    icon: TrendingUp },
                  { label: 'Used',      value: `${myAllowance.used_hours}h`,      tone: 'violet',  icon: Check },
                  { label: 'Total',     value: `${myAllowance.total_hours + myAllowance.extra_hours}h`, tone: 'slate', icon: Briefcase, bonus: myAllowance.extra_hours },
                ].map((m, i) => (
                  <div key={i} className={`relative p-4 rounded-2xl bg-${m.tone}-500/[0.06] border border-${m.tone}-500/20 hover:border-${m.tone}-500/40 transition-all group overflow-hidden`}>
                    <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full bg-${m.tone}-500/10 blur-2xl group-hover:bg-${m.tone}-500/15 transition-colors`} />
                    <div className="relative flex items-start justify-between">
                      <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">{m.label}</p>
                        <p className={`text-2xl font-bold text-${m.tone}-300 leading-none`}>{m.value}</p>
                        {m.bonus > 0 && (
                          <p className="text-[10px] text-emerald-400 mt-1.5">+{m.bonus}h bonus</p>
                        )}
                      </div>
                      <m.icon className={`w-4 h-4 text-${m.tone}-400/60`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ QUICK STATS ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Pending',        value: myRequests.filter(r => r.status === 'pending').length,  icon: Hourglass,   accent: 'amber' },
          { label: 'Approved',       value: myRequests.filter(r => r.status === 'approved').length, icon: CheckCircle, accent: 'emerald' },
          { label: 'Hours This Month', value: clockData.total_time || '0h',                          icon: Clock,       accent: 'cyan' },
          { label: 'Upcoming Shifts', value: shifts.filter(s => new Date(s.date) >= new Date()).length, icon: Calendar,   accent: 'violet' },
        ].map((stat, i) => (
          <div key={i} className={`group relative overflow-hidden rounded-2xl border border-${stat.accent}-500/15 bg-[#0a0a14]/70 backdrop-blur-sm hover:border-${stat.accent}-500/35 hover:-translate-y-0.5 transition-all p-4`}>
            <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-${stat.accent}-500/[0.08] blur-2xl group-hover:bg-${stat.accent}-500/[0.15] transition-colors`} />
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-white/40 text-[10px] uppercase tracking-widest mb-1.5">{stat.label}</p>
                <p className={`text-2xl font-bold text-${stat.accent}-300 leading-none`}>{stat.value}</p>
              </div>
              <div className={`w-9 h-9 rounded-xl bg-${stat.accent}-500/10 border border-${stat.accent}-500/20 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <stat.icon className={`w-4 h-4 text-${stat.accent}-400`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ═══ VIEW TOGGLE — Jewel segmented rail ═══ */}
      <div className="relative overflow-x-auto scrollbar-hide">
        <div className="inline-flex items-center gap-1 p-1.5 bg-gradient-to-r from-[#0a0a14]/90 via-[#0d0b1f]/90 to-[#0a0a14]/90 backdrop-blur-xl rounded-2xl border border-white/[0.06] min-w-full shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_10px_40px_-10px_rgba(0,229,255,0.15)] relative">
          {/* Hairline top light */}
          <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent pointer-events-none" />
          {[
            { id: 'calendar',     label: 'Calendar',        icon: Calendar,   accent: 'cyan',    badge: null },
            { id: 'my-shifts',    label: 'My Shifts',       icon: Briefcase,  accent: 'emerald', badge: null },
            { id: 'overtime',     label: 'Overtime',        icon: Zap,        accent: 'amber',   badge: null, highlight: true },
            { id: 'my-requests',  label: 'My Requests',     icon: User,       accent: 'violet',  badge: myRequests.filter(r => r.status === 'pending').length },
            ...(canApproveLeave ? [{ id: 'all-requests', label: 'Leave Queue',    icon: AlertCircle, accent: 'rose',    badge: pendingLeaveCount }] : []),
            ...(canViewAllShifts ? [{ id: 'roster',       label: 'Team Roster',    icon: Users,       accent: 'teal',    badge: null }] : []),
            ...(canViewAllShifts ? [{ id: 'shifts',       label: 'Shift Overview', icon: BarChart3,   accent: 'blue',    badge: isExecutive ? pendingShiftCount : 0 }] : []),
          ].map(tab => {
            const active = viewMode === tab.id;
            const accentMap = {
              cyan:    { grad: 'from-cyan-500/25 to-violet-500/15',   ring: 'border-cyan-400/40',    text: 'text-cyan-200',    glow: 'shadow-[0_0_24px_rgba(0,229,255,0.25)]',   dot: 'bg-cyan-400' },
              emerald: { grad: 'from-emerald-500/25 to-teal-500/15',  ring: 'border-emerald-400/40', text: 'text-emerald-200', glow: 'shadow-[0_0_24px_rgba(52,211,153,0.25)]',  dot: 'bg-emerald-400' },
              amber:   { grad: 'from-amber-500/25 to-orange-500/15',  ring: 'border-amber-400/40',   text: 'text-amber-200',   glow: 'shadow-[0_0_24px_rgba(251,191,36,0.25)]',  dot: 'bg-amber-400' },
              violet:  { grad: 'from-violet-500/25 to-purple-500/15', ring: 'border-violet-400/40',  text: 'text-violet-200',  glow: 'shadow-[0_0_24px_rgba(138,43,226,0.25)]',  dot: 'bg-violet-400' },
              rose:    { grad: 'from-rose-500/25 to-pink-500/15',     ring: 'border-rose-400/40',    text: 'text-rose-200',    glow: 'shadow-[0_0_24px_rgba(244,63,94,0.25)]',   dot: 'bg-rose-400' },
              teal:    { grad: 'from-teal-500/25 to-cyan-500/15',     ring: 'border-teal-400/40',    text: 'text-teal-200',    glow: 'shadow-[0_0_24px_rgba(45,212,191,0.25)]',  dot: 'bg-teal-400' },
              blue:    { grad: 'from-sky-500/25 to-indigo-500/15',    ring: 'border-sky-400/40',     text: 'text-sky-200',     glow: 'shadow-[0_0_24px_rgba(56,189,248,0.25)]',  dot: 'bg-sky-400' },
            }[tab.accent];
            return (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id)}
                data-testid={`view-tab-${tab.id}`}
                className={`group relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all overflow-hidden ${
                  active
                    ? `bg-gradient-to-br ${accentMap.grad} border ${accentMap.ring} ${accentMap.text} ${accentMap.glow}`
                    : 'text-white/50 hover:text-white/90 hover:bg-white/[0.03] border border-transparent'
                }`}
              >
                {active && (
                  <>
                    <span className="absolute inset-x-3 -bottom-px h-[2px] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                    <span className={`absolute top-1 right-1 w-1 h-1 rounded-full ${accentMap.dot} animate-pulse`} />
                  </>
                )}
                <tab.icon className={`w-4 h-4 transition-transform ${active ? 'scale-110' : 'group-hover:scale-105'}`} strokeWidth={active ? 2 : 1.5} />
                <span className="tracking-wide">{tab.label}</span>
                {tab.badge > 0 && (
                  <Badge className={`h-5 min-w-[20px] px-1.5 text-[10px] font-semibold border-0 rounded-full animate-pulse ${
                    active ? 'bg-white/25 text-white' : 'bg-rose-500 text-white shadow-[0_0_12px_rgba(244,63,94,0.6)]'
                  }`}>{tab.badge}</Badge>
                )}
                {tab.highlight && !active && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card className="bg-white/[0.02] border-white/[0.06] overflow-hidden">
          <CardHeader className="border-b border-white/[0.05] pb-4 bg-gradient-to-r from-[#00E5FF]/[0.04] via-transparent to-[#8A2BE2]/[0.04]">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white flex items-center gap-2.5 text-base font-light">
                <Calendar className="w-5 h-5 text-[#00E5FF]" />
                {monthYear}
              </CardTitle>
              <div className="flex items-center gap-1 p-0.5 rounded-full bg-white/[0.02] border border-white/[0.06]">
                <Button size="sm" variant="ghost" onClick={() => navigateMonth(-1)} data-testid="calendar-prev-month" className="text-white/50 hover:text-white hover:bg-white/[0.05] h-8 w-8 p-0 rounded-full">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setCurrentMonth(new Date())} className="text-white/60 hover:text-[#00E5FF] hover:bg-white/[0.05] h-8 px-3 text-[10px] uppercase tracking-[0.18em] rounded-full">
                  Today
                </Button>
                <Button size="sm" variant="ghost" onClick={() => navigateMonth(1)} data-testid="calendar-next-month" className="text-white/50 hover:text-white hover:bg-white/[0.05] h-8 w-8 p-0 rounded-full">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Quick filter chips */}
            <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-white/[0.05]">
              <span className="text-[10px] tracking-[0.18em] uppercase text-white/40 mr-1">Focus:</span>
              {[
                { id: 'all',          label: 'All',            icon: Calendar,   accent: 'from-white/10 to-white/5',       ring: 'border-white/20',       text: 'text-white' },
                { id: 'this-week',    label: 'This Week',      icon: CalendarDays, accent: 'from-cyan-500/25 to-violet-500/15', ring: 'border-cyan-400/40',   text: 'text-cyan-200' },
                { id: 'next-week',    label: 'Next Week',      icon: ChevronRight, accent: 'from-emerald-500/25 to-cyan-500/15', ring: 'border-emerald-400/40',text: 'text-emerald-200' },
                { id: 'pending-only', label: 'Pending Only',   icon: Hourglass,  accent: 'from-amber-500/25 to-orange-500/15', ring: 'border-amber-400/40',  text: 'text-amber-200' },
              ].map(chip => {
                const active = calendarDateFilter === chip.id;
                const count = chip.id === 'pending-only'
                  ? myRequests.filter(r => r.status === 'pending').length
                  : null;
                return (
                  <button
                    key={chip.id}
                    onClick={() => setCalendarDateFilter(chip.id)}
                    data-testid={`calendar-filter-${chip.id}`}
                    className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide border transition-all ${
                      active
                        ? `bg-gradient-to-r ${chip.accent} ${chip.ring} ${chip.text} shadow-[0_0_18px_-6px_rgba(0,229,255,0.35)]`
                        : 'bg-white/[0.02] border-white/[0.06] text-white/45 hover:text-white/90 hover:border-white/[0.15]'
                    }`}
                  >
                    <chip.icon className={`w-3 h-3 transition-transform ${active ? 'scale-110' : 'group-hover:scale-105'}`} />
                    <span>{chip.label}</span>
                    {count > 0 && (
                      <span className={`inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold ${
                        active ? 'bg-white/25 text-white' : 'bg-amber-500/20 text-amber-300'
                      }`}>{count}</span>
                    )}
                    {active && <span className="w-1 h-1 rounded-full bg-current animate-pulse ml-0.5" />}
                  </button>
                );
              })}
              {calendarDateFilter !== 'all' && (
                <button onClick={() => setCalendarDateFilter('all')} className="ml-auto text-white/40 hover:text-white text-[10px] tracking-wide inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>

            {/* Legend + Heatmap toggle */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex flex-wrap gap-3 text-[11px]">
                {showHeatmap && canViewAllShifts ? (
                  <>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-white/40 mr-1 self-center">Coverage:</span>
                    {Object.entries(HEATMAP_STYLES).map(([lvl, style]) => (
                      <div key={lvl} className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                        <span className={`${style.text}`}>{style.label}</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#00E5FF]"></span><span className="text-white/50">Clock Hours</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#8A2BE2]"></span><span className="text-white/50">Regular Shift</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400"></span><span className="text-white/50">Assigned Shift</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-400"></span><span className="text-white/50">Leave</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-[#F59E0B]/25 text-[#fbbf24] font-bold flex items-center justify-center text-[9px]">P</span><span className="text-white/50">Payday</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-rose-400 to-amber-400"></span><span className="text-white/50">UK Holiday <span className="text-white/30 text-[10px]">· auto-off</span></span></div>
                  </>
                )}
              </div>
              {canViewAllShifts && (
                <button
                  type="button"
                  onClick={() => setShowHeatmap(v => !v)}
                  data-testid="heatmap-toggle"
                  className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all ${
                    showHeatmap
                      ? 'bg-gradient-to-r from-[#00E5FF]/15 to-[#8A2BE2]/15 border-[#00E5FF]/30 text-[#00E5FF] shadow-[0_0_16px_rgba(0,229,255,0.10)]'
                      : 'bg-white/[0.02] border-white/10 text-white/50 hover:text-white hover:border-white/20'
                  }`}
                  aria-pressed={showHeatmap}
                >
                  <TrendingUp className={`w-3.5 h-3.5 ${showHeatmap ? 'text-[#00E5FF]' : ''}`} />
                  Heatmap {showHeatmap ? 'on' : 'off'}
                  <span className={`w-6 h-3 rounded-full relative transition-colors ${showHeatmap ? 'bg-[#00E5FF]/40' : 'bg-white/[0.06]'}`}>
                    <span className={`absolute top-0.5 w-2 h-2 rounded-full bg-white transition-all ${showHeatmap ? 'left-3.5' : 'left-0.5'}`} />
                  </span>
                </button>
              )}
            </div>

            {/* UK Bank Holidays this month */}
            {(() => {
              const monthHolidays = holidaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth());
              if (!monthHolidays.length) return null;
              return (
                <div className="relative overflow-hidden rounded-2xl border border-rose-400/25 bg-gradient-to-r from-rose-500/[0.06] via-amber-500/[0.04] to-emerald-500/[0.06] p-3 mb-3" data-testid="uk-holidays-banner">
                  <div className="pointer-events-none absolute -top-16 -right-8 w-40 h-40 rounded-full blur-[80px] opacity-40 bg-rose-500/30" />
                  <div className="relative flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 pr-3 border-r border-white/[0.08]">
                      <div className="w-9 h-9 rounded-xl bg-rose-500/15 border border-rose-400/30 flex items-center justify-center">
                        <PartyPopper className="w-4 h-4 text-rose-300" strokeWidth={1.7} />
                      </div>
                      <div>
                        <p className="text-[9px] tracking-[0.22em] uppercase text-white/40 font-medium">UK Bank Holidays</p>
                        <p className="text-white text-xs font-medium">This month · staff auto-off</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {monthHolidays.map(h => {
                        const hs = HOLIDAY_TONE_STYLES[h.tone];
                        const day = new Date(h.date + 'T00:00:00').getDate();
                        return (
                          <span key={h.date} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border ${hs.chipBg} ${hs.chipText} ${hs.chipBorder}`} title={h.name}>
                            <span>{h.emoji}</span>
                            <span className="tabular-nums font-semibold">{day}</span>
                            <span className="opacity-80">{h.name}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-white/40 text-[10px] font-medium uppercase tracking-[0.18em] py-2">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((date, index) => {
                const { leaves, shifts: dayShifts, paydays, clockRecords, regularShifts } = getEventsForDate(date);
                const isToday = date && date.toDateString() === new Date().toDateString();
                const totalClockMins = clockRecords.reduce((sum, r) => sum + (r.total_minutes || 0), 0);
                const isWeekend = date && (date.getDay() === 0 || date.getDay() === 6);
                const density = showHeatmap && canViewAllShifts ? getDensity(date) : null;
                const hmStyle = density ? HEATMAP_STYLES[density.level] : null;
                const holiday = getUkHoliday(date);
                const holidayStyle = holiday ? HOLIDAY_TONE_STYLES[holiday.tone] : null;

                // Filter chip logic — dim cells outside focus range
                let inFocus = true;
                if (date && calendarDateFilter !== 'all') {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const dow = today.getDay();
                  const startOfThisWeek = new Date(today); startOfThisWeek.setDate(today.getDate() - dow);
                  const endOfThisWeek = new Date(startOfThisWeek); endOfThisWeek.setDate(startOfThisWeek.getDate() + 6);
                  const startOfNextWeek = new Date(endOfThisWeek); startOfNextWeek.setDate(endOfThisWeek.getDate() + 1);
                  const endOfNextWeek = new Date(startOfNextWeek); endOfNextWeek.setDate(startOfNextWeek.getDate() + 6);
                  if (calendarDateFilter === 'this-week') inFocus = date >= startOfThisWeek && date <= endOfThisWeek;
                  else if (calendarDateFilter === 'next-week') inFocus = date >= startOfNextWeek && date <= endOfNextWeek;
                  else if (calendarDateFilter === 'pending-only') {
                    inFocus = leaves.some(l => l.status === 'pending') || dayShifts.some(s => s.status === 'pending');
                  }
                }

                return (
                  <div
                    key={index}
                    onClick={() => date && handleDayClick(date)}
                    className={`relative min-h-[92px] p-1.5 rounded-xl border transition-all ${!inFocus ? 'opacity-30 grayscale' : ''} ${
                      date
                        ? `cursor-pointer ${
                            isToday
                              ? 'border-[#00E5FF]/40 bg-[#00E5FF]/[0.08] shadow-[inset_0_0_0_1px_rgba(0,229,255,0.15),0_0_20px_rgba(0,229,255,0.08)]'
                              : holidayStyle && !hmStyle
                                ? `${holidayStyle.cellBorder} ${holidayStyle.cellBg} ${holidayStyle.cellRing} hover:brightness-125`
                                : hmStyle
                                  ? `border-transparent ${hmStyle.tint} ring-1 ring-inset ${hmStyle.ring} hover:brightness-125`
                                  : isWeekend
                                    ? 'border-white/[0.04] bg-white/[0.01] hover:border-[#00E5FF]/30 hover:bg-white/[0.03]'
                                    : 'border-white/[0.06] bg-white/[0.02] hover:border-[#00E5FF]/30 hover:bg-white/[0.03]'
                          }`
                        : 'border-transparent'
                    }`}
                    data-testid={date ? `calendar-day-${date.getDate()}` : ''}
                    title={holiday ? `${holiday.name} · UK Bank Holiday — all staff auto-off${density ? ` · ${density.effective}/${density.target} on shift` : ''}` : density ? `${hmStyle.label} · ${density.effective}/${density.target} on shift · ${density.on_leave} on leave` : undefined}
                  >
                    {date && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium tabular-nums ${
                            isToday ? 'text-[#00E5FF]' : isWeekend ? 'text-white/40' : 'text-white/80'
                          }`}>
                            {date.getDate()}
                          </span>
                          <div className="flex items-center gap-1">
                            {density && !isToday && (
                              <span
                                className={`inline-flex items-center gap-0.5 px-1 h-[15px] rounded ${hmStyle.tint} ${hmStyle.text} border ${hmStyle.ring.replace('ring-', 'border-').replace('/50', '/40').replace('/40', '/30')} text-[9px] font-semibold`}
                                data-testid={`density-badge-${date.getDate()}`}
                              >
                                <span className={`w-1 h-1 rounded-full ${hmStyle.dot}`} />
                                {density.effective}
                              </span>
                            )}
                            {paydays.length > 0 && (
                              <span className="w-5 h-5 rounded bg-[#F59E0B]/30 flex items-center justify-center text-[#fbbf24] font-bold text-[10px]">
                                P
                              </span>
                            )}
                            {holiday && (
                              <span
                                className={`inline-flex items-center gap-0.5 px-1 h-[15px] rounded ${holidayStyle.chipBg} ${holidayStyle.chipText} border ${holidayStyle.chipBorder} text-[9px] font-semibold`}
                                data-testid={`uk-holiday-badge-${date.getDate()}`}
                                title={`${holiday.name} · UK bank holiday`}
                              >
                                <span className={`w-1 h-1 rounded-full ${holidayStyle.dot}`} />
                                UK
                              </span>
                            )}
                          </div>
                        </div>
                        {holiday && (
                          <div
                            className={`mt-1 px-1 py-0.5 rounded text-[9px] font-medium truncate ${holidayStyle.chipBg} ${holidayStyle.chipText} border ${holidayStyle.chipBorder}`}
                            data-testid={`uk-holiday-name-${date.getDate()}`}
                          >
                            <span className="mr-0.5">{holiday.emoji}</span>{holiday.name}
                          </div>
                        )}
                        <div className="space-y-0.5 mt-1">
                          {holiday && (
                            <div className="text-[9px] px-1 py-0.5 rounded truncate bg-white/[0.05] text-white/60 italic border border-white/[0.06]">
                              All staff off
                            </div>
                          )}
                          {totalClockMins > 0 && (
                            <div className="text-[10px] px-1 py-0.5 rounded truncate bg-[#00E5FF]/25 text-[#00E5FF]">
                              <Clock className="w-2 h-2 inline mr-0.5" />
                              {Math.floor(totalClockMins / 60)}h{totalClockMins % 60 > 0 ? ` ${totalClockMins % 60}m` : ''}
                            </div>
                          )}
                          {regularShifts.slice(0, 1).map((shift, i) => (
                            <div key={`reg-${i}`} className="text-[10px] px-1 py-0.5 rounded truncate bg-[#8A2BE2]/35 text-[#c9a4f0]">
                              <Calendar className="w-2 h-2 inline mr-0.5" />
                              {shift.display_name?.split(' ')[0]}
                            </div>
                          ))}
                          {dayShifts.slice(0, 1).map((shift, i) => (
                            <div key={`shift-${i}`} className={`text-[10px] px-1 py-0.5 rounded truncate ${getShiftTypeInfo(shift.shift_type).color} text-white`}>
                              <Timer className="w-2 h-2 inline mr-0.5" />
                              {shift.staff_display_name?.split(' ')[0]}
                            </div>
                          ))}
                          {leaves.slice(0, 1).map((event, i) => (
                            <div key={`leave-${i}`} className={`text-[10px] px-1 py-0.5 rounded truncate ${getLeaveTypeInfo(event.leave_type).color} text-white ${event.status === 'pending' ? 'opacity-60' : ''}`}>
                              {event.requested_by_name?.split(' ')[0]}
                            </div>
                          ))}
                          {(dayShifts.length + leaves.length + regularShifts.length) > 2 && (
                            <div className="text-[10px] text-white/40">+{dayShifts.length + leaves.length + regularShifts.length - 2} more</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overtime Marketplace */}
      {viewMode === 'overtime' && (
        <OvertimeMarketplace getAuthHeaders={getAuthHeaders} currentUser={currentUser} />
      )}

      {/* My Shifts View (visible to all staff) */}
      {viewMode === 'my-shifts' && (
        <div className="space-y-4">
          {/* Hero summary */}
          <Card className="bg-white/[0.02] border-white/[0.06] overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-purple-500/5 pointer-events-none" />
            <CardContent className="p-5 relative">
              <div className="flex flex-wrap items-center gap-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/20 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">My Shifts</p>
                    <p className="text-white/40 text-xs">Your personal schedule at a glance</p>
                  </div>
                </div>
                {myShifts.summary?.next_shift ? (
                  <div className="ml-auto flex items-center gap-3 px-4 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-cyan-400/80">Next Shift</p>
                      <p className="text-white font-semibold mt-0.5 text-sm">
                        {new Date(myShifts.summary.next_shift.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                        <span className="text-white/40 font-normal mx-1.5">·</span>
                        <span className="text-cyan-300 tabular-nums">{myShifts.summary.next_shift.start_time}–{myShifts.summary.next_shift.end_time}</span>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="ml-auto px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.08]">
                    <p className="text-white/40 text-xs italic">No upcoming shifts scheduled</p>
                  </div>
                )}
              </div>
              {/* Stats */}
              <div className="grid grid-cols-3 mt-4 pt-4 border-t border-white/[0.06]">
                {[
                  { label: 'Upcoming', value: myShifts.summary?.upcoming_count ?? myShifts.upcoming.length, color: 'text-cyan-400' },
                  { label: 'Hours scheduled', value: `${myShifts.summary?.total_upcoming_hours ?? 0}h`, color: 'text-purple-400' },
                  { label: 'Last 30 days', value: myShifts.summary?.past_count ?? myShifts.past.length, color: 'text-white/70' },
                ].map((s, i) => (
                  <div key={i} className={i < 2 ? 'border-r border-white/[0.06] pr-4' : 'pl-4'}>
                    <p className="text-[10px] uppercase tracking-wider text-white/40">{s.label}</p>
                    <p className={`text-2xl font-bold mt-1 tabular-nums ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming / Past sub-toggle — Jewel */}
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
            {[
              { id: 'upcoming', label: 'Upcoming', count: myShifts.upcoming.length },
              { id: 'past', label: 'Past', count: myShifts.past.length },
            ].map(t => {
              const a = myShiftsView === t.id;
              return (
                <button key={t.id} onClick={() => setMyShiftsView(t.id)}
                  data-testid={`my-shifts-sub-${t.id}`}
                  className={`px-4 py-1.5 rounded-full text-[11px] font-medium tracking-wide transition-all inline-flex items-center gap-1.5 ${
                    a ? 'bg-gradient-to-r from-cyan-500/25 to-violet-500/15 text-white border border-cyan-400/30 shadow-[0_0_14px_-4px_rgba(0,229,255,0.4)]'
                      : 'text-white/50 hover:text-white border border-transparent'
                  }`}>
                  {t.label}
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[9px] font-bold ${a ? 'bg-white/20 text-white' : 'bg-white/[0.04] text-white/50'}`}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {/* Shifts list */}
          {(myShiftsView === 'upcoming' ? myShifts.upcoming : myShifts.past).length === 0 ? (
            <Card className="bg-[#0a0d1a] border-white/[0.06]">
              <CardContent className="text-center py-12">
                <Briefcase className="w-10 h-10 text-white/10 mx-auto mb-3" />
                <p className="text-white/50 text-sm">{myShiftsView === 'upcoming' ? 'No upcoming shifts on your roster.' : 'No shifts in the past 30 days.'}</p>
                {myShiftsView === 'upcoming' && (
                  <p className="text-white/30 text-xs mt-1">Check back soon — shifts are posted by your manager.</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2.5" data-testid="my-shifts-list">
              {(myShiftsView === 'upcoming' ? myShifts.upcoming : myShifts.past).map((s, idx) => {
                const d = new Date(s.date + 'T00:00:00');
                const isToday = s.is_today;
                const isPast = myShiftsView === 'past';
                const dayOfWeek = d.toLocaleDateString(undefined, { weekday: 'short' });
                const dayNum = d.getDate();
                const monthShort = d.toLocaleDateString(undefined, { month: 'short' });
                const typeMeta = ({
                  regular: { color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
                  overtime: { color: 'text-violet-300', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
                  training: { color: 'text-sky-300', bg: 'bg-sky-500/10', border: 'border-sky-500/20' },
                  'on-call': { color: 'text-violet-300', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
                }[s.shift_type] || { color: 'text-white/70', bg: 'bg-white/[0.04]', border: 'border-white/[0.08]' });
                return (
                  <div key={s.id || idx}
                    data-testid={`my-shift-${s.id || idx}`}
                    className={`group relative flex items-center gap-4 p-4 rounded-xl border transition-all ${
                      isToday
                        ? 'bg-cyan-500/10 border-cyan-500/30 shadow-[0_4px_20px_rgba(34,211,238,0.10)]'
                        : isPast
                          ? 'bg-[#0a0d1a] border-white/[0.06] opacity-75 hover:opacity-100'
                          : 'bg-white/[0.02] border-white/[0.06] hover:border-cyan-500/30 hover:bg-white/[0.02]'
                    }`}>
                    {/* Date block */}
                    <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center border ${
                      isToday ? 'bg-gradient-to-br from-cyan-500 to-purple-600 text-white border-cyan-400 shadow-lg shadow-cyan-500/30' : 'bg-white/[0.02] border-white/[0.08] text-white/80'
                    }`}>
                      <span className={`text-[9px] font-bold uppercase tracking-wider ${isToday ? 'text-white/80' : 'text-white/40'}`}>{dayOfWeek}</span>
                      <span className="text-xl font-bold leading-none">{dayNum}</span>
                      <span className={`text-[9px] uppercase tracking-wider mt-0.5 ${isToday ? 'text-white/70' : 'text-white/40'}`}>{monthShort}</span>
                    </div>

                    {/* Time + type */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold text-base tabular-nums">
                          {s.start_time} <span className="text-white/40 mx-1">→</span> {s.end_time}
                        </span>
                        {isToday && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">Today</span>
                        )}
                        <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full border ${typeMeta.color} ${typeMeta.bg} ${typeMeta.border}`}>
                          {s.shift_type || 'regular'}
                        </span>
                        {s.hours_worked > 0 && (
                          <span className="text-white/40 text-[11px]">· {s.hours_worked}h</span>
                        )}
                      </div>
                      {s.notes && (
                        <p className="text-white/50 text-xs mt-1.5 italic line-clamp-1">"{s.notes}"</p>
                      )}
                    </div>

                    {/* Status pill */}
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]" />
                        Confirmed
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* My Requests View */}
      {/* My Requests — Jewel */}
      {viewMode === 'my-requests' && (
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md">
          <div className="pointer-events-none absolute -top-24 right-1/4 w-60 h-40 bg-violet-500/[0.10] rounded-full blur-[100px]" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/40 to-transparent" />
          {/* Header */}
          <div className="relative px-5 py-4 flex items-center justify-between flex-wrap gap-3 border-b border-white/[0.05]">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500/25 to-purple-500/15 border border-violet-400/30 flex items-center justify-center shadow-[0_0_20px_-6px_rgba(138,43,226,0.4)]">
                <User className="w-5 h-5 text-violet-200" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Personal</p>
                <h2 className="text-white text-lg font-light tracking-tight">My <span className="bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent font-medium">Requests</span></h2>
              </div>
            </div>
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
              {['all', 'pending', 'approved', 'rejected'].map(status => {
                const active = statusFilter === status;
                return (
                  <button key={status} onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide transition-all ${
                      active ? 'bg-gradient-to-r from-violet-500/25 to-cyan-500/20 text-white border border-violet-400/30 shadow-[0_0_14px_-4px_rgba(138,43,226,0.5)]' : 'text-white/50 hover:text-white border border-transparent'
                    }`}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative p-4">
            {myRequests.length === 0 ? (
              <div className="text-center py-14">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/[0.08] border border-cyan-500/20 mb-4">
                  <Calendar className="w-7 h-7 text-cyan-300" strokeWidth={1.5} />
                </div>
                <p className="text-white/70 tracking-wide">No requests yet</p>
                <p className="text-white/35 text-xs mt-1">Tap Request Leave to submit your first one.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {myRequests.filter(r => statusFilter === 'all' || r.status === statusFilter).map(request => {
                  const typeInfo = getLeaveTypeInfo(request.leave_type);
                  const isExpanded = expandedRequest === request.id;
                  const startDate = new Date(request.start_date);
                  const endDate = new Date(request.end_date);
                  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                  const hours = days * 8;
                  const tone = request.status === 'pending' ? { border: 'border-amber-400/30', bg: 'bg-gradient-to-br from-amber-500/[0.06] to-transparent', bar: 'from-amber-400 to-orange-500' }
                    : request.status === 'approved' ? { border: 'border-emerald-400/25', bg: 'bg-gradient-to-br from-emerald-500/[0.05] to-transparent', bar: 'from-emerald-400 to-teal-500' }
                    : request.status === 'rejected' ? { border: 'border-rose-400/25', bg: 'bg-gradient-to-br from-rose-500/[0.05] to-transparent', bar: 'from-rose-400 to-pink-500' }
                    : { border: 'border-white/[0.06]', bg: 'bg-white/[0.02]', bar: 'from-white/20 to-white/10' };
                  return (
                    <div key={request.id} className={`relative rounded-2xl border ${tone.border} ${tone.bg} overflow-hidden hover:border-white/[0.15] transition-all`}>
                      <span className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${tone.bar}`} />
                      <button onClick={() => setExpandedRequest(isExpanded ? null : request.id)} className="w-full p-4 pl-5 text-left">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                              {getStatusIcon(request.status)}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${typeInfo.textColor} bg-white/[0.02] border-white/[0.08]`}>
                                  {typeInfo.label}
                                </span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-[0.05em] uppercase ${getStatusBadge(request.status)}`}>
                                  {request.status}
                                </span>
                                <span className="text-white/40 text-[10px]">· {hours}h / {days}d</span>
                              </div>
                              <p className="text-white font-medium text-sm truncate">{formatDate(request.start_date)} <span className="text-white/50 mx-1.5">→</span> {formatDate(request.end_date)}</p>
                            </div>
                          </div>
                          <ChevronRight className={`w-4 h-4 text-white/40 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90 text-cyan-300' : ''}`} />
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-white/[0.06] px-4 pb-4 pt-3 pl-5 space-y-3 bg-black/20">
                          <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-3">
                            <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Reason</p>
                            <p className="text-white/80 text-sm">{request.reason || <span className="italic text-white/30">No reason provided</span>}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-2.5">
                              <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Submitted</p>
                              <p className="text-white/75 text-xs">{formatDate(request.created_at)}</p>
                            </div>
                            {request.reviewed_at && (
                              <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-2.5">
                                <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Reviewed</p>
                                <p className="text-white/75 text-xs">{formatDate(request.reviewed_at)}</p>
                              </div>
                            )}
                          </div>
                          {request.reviewed_by && (
                            <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-3">
                              <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">{request.status === 'approved' ? 'Approved by' : 'Rejected by'}</p>
                              <p className="text-white text-sm">{request.reviewed_by_name || request.reviewed_by}</p>
                              {request.review_notes && <p className="text-white/50 text-xs mt-1.5 italic">"{request.review_notes}"</p>}
                            </div>
                          )}
                          {request.status === 'pending' && (
                            <div className="flex justify-end pt-1">
                              <button onClick={(e) => { e.stopPropagation(); handleWithdrawRequest(request.id); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500/[0.08] border border-rose-500/25 text-rose-300 hover:bg-rose-500/[0.15] text-[11px] font-medium tracking-wide transition-all">
                                <RotateCcw className="w-3 h-3" /> Withdraw request
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Requests View (Manager/Executive) */}
      {/* Leave Queue (Manager/Executive) — Jewel */}
      {viewMode === 'all-requests' && canApproveLeave && (
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md">
          <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full blur-[100px] opacity-30 bg-cyan-500/25" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 rounded-full blur-[100px] opacity-25 bg-violet-500/25" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />

          {/* Header */}
          <div className="relative px-5 py-4 flex items-start justify-between flex-wrap gap-3 border-b border-white/[0.05]">
            <div className="flex items-center gap-3">
              <div className="relative w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-500/25 to-violet-500/15 border border-cyan-400/30 flex items-center justify-center shadow-[0_0_20px_-6px_rgba(0,229,255,0.4)]">
                <Shield className="w-5 h-5 text-cyan-200" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Approvals · Line manager</p>
                <div className="flex items-center gap-2">
                  <h2 className="text-white text-lg font-light tracking-tight">Leave <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent font-medium">Requests</span></h2>
                  {allRequests.filter(r => r.status === 'pending').length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/[0.10] border border-amber-500/25 text-amber-300 text-[10px] tracking-[0.05em] font-medium animate-pulse">
                      <Hourglass className="w-3 h-3" />
                      {allRequests.filter(r => r.status === 'pending').length} pending
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
              {['all', 'pending', 'approved', 'rejected'].map(status => {
                const active = statusFilter === status;
                const cnt = status === 'all' ? allRequests.length : allRequests.filter(r => r.status === status).length;
                const tone = status === 'pending' ? 'amber' : status === 'approved' ? 'emerald' : status === 'rejected' ? 'rose' : 'cyan';
                return (
                  <button key={status} onClick={() => setStatusFilter(status)}
                    data-testid={`queue-filter-${status}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide transition-all ${
                      active
                        ? tone === 'amber' ? 'bg-amber-500/15 text-amber-200 border border-amber-400/30 shadow-[0_0_14px_-4px_rgba(251,191,36,0.4)]'
                        : tone === 'emerald' ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/30 shadow-[0_0_14px_-4px_rgba(52,211,153,0.4)]'
                        : tone === 'rose' ? 'bg-rose-500/15 text-rose-200 border border-rose-400/30 shadow-[0_0_14px_-4px_rgba(244,63,94,0.4)]'
                        : 'bg-gradient-to-r from-cyan-500/20 to-violet-500/15 text-white border border-cyan-400/30 shadow-[0_0_14px_-4px_rgba(0,229,255,0.4)]'
                        : 'text-white/50 hover:text-white border border-transparent'
                    }`}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                    {cnt > 0 && <span className={`ml-1.5 opacity-70 text-[10px]`}>{cnt}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative p-4">
            {filteredAllRequests.length === 0 ? (
              <div className="text-center py-14">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/[0.08] border border-emerald-500/20 mb-4">
                  <CheckCircle className="w-7 h-7 text-emerald-300" strokeWidth={1.5} />
                </div>
                <p className="text-white/70 tracking-wide">All caught up</p>
                <p className="text-white/35 text-xs mt-1">Nothing waiting for your review right now.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {filteredAllRequests.map(request => {
                  const typeInfo = getLeaveTypeInfo(request.leave_type);
                  const startDate = new Date(request.start_date);
                  const endDate = new Date(request.end_date);
                  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
                  const hours = days * 8;
                  const statusStyle = request.status === 'pending'
                    ? { border: 'border-amber-400/30', bg: 'bg-gradient-to-br from-amber-500/[0.06] via-white/[0.02] to-transparent', glow: 'shadow-[0_0_28px_-12px_rgba(251,191,36,0.5)]', dot: 'bg-amber-400' }
                    : request.status === 'approved'
                    ? { border: 'border-emerald-400/25', bg: 'bg-gradient-to-br from-emerald-500/[0.05] via-white/[0.02] to-transparent', glow: '', dot: 'bg-emerald-400' }
                    : request.status === 'rejected'
                    ? { border: 'border-rose-400/25', bg: 'bg-gradient-to-br from-rose-500/[0.05] via-white/[0.02] to-transparent', glow: '', dot: 'bg-rose-400' }
                    : { border: 'border-white/[0.06]', bg: 'bg-white/[0.02]', glow: '', dot: 'bg-white/30' };
                  return (
                    <div key={request.id}
                      data-testid={`leave-queue-item-${request.id}`}
                      className={`relative rounded-2xl border ${statusStyle.border} ${statusStyle.bg} ${statusStyle.glow} transition-all hover:border-white/[0.15] overflow-hidden`}>
                      {request.status === 'pending' && <span className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 via-amber-500 to-orange-500" />}
                      <div className="p-4 pl-5">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            {/* Header row */}
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/25 to-violet-500/15 border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                                <span className="text-white text-sm font-semibold">{(request.requested_by_name || '?').charAt(0).toUpperCase()}</span>
                              </div>
                              <span className="text-white font-medium text-base">{request.requested_by_name}</span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${typeInfo.textColor} bg-white/[0.02]`} style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                                <span className="w-1 h-1 rounded-full bg-current" /> {typeInfo.label}
                              </span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-[0.05em] uppercase ${getStatusBadge(request.status)}`}>
                                <span className={`w-1 h-1 rounded-full ${statusStyle.dot} ${request.status === 'pending' ? 'animate-pulse' : ''}`} />
                                {request.status}
                              </span>
                              <span className="text-white/40 text-[11px] font-mono">{hours}h · {days}d</span>
                            </div>
                            {/* Duration + submitted */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                              <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                                <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Duration</p>
                                <p className="text-white/85 text-sm font-medium">
                                  {formatDate(request.start_date)} <span className="text-cyan-300 mx-1.5">→</span> {formatDate(request.end_date)}
                                </p>
                              </div>
                              <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                                <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Submitted</p>
                                <p className="text-white/85 text-sm font-medium">{formatDate(request.created_at)}</p>
                              </div>
                            </div>
                            {/* Reason */}
                            <div className="rounded-xl border border-white/[0.05] bg-white/[0.02] p-3">
                              <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-1">Reason</p>
                              <p className="text-white/80 text-sm leading-relaxed">{request.reason || <span className="italic text-white/30">No reason provided</span>}</p>
                            </div>
                          </div>
                          {request.status === 'pending' && (
                            <button onClick={() => openReviewModal(request)}
                              data-testid={`review-btn-${request.id}`}
                              className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 text-white text-xs font-semibold tracking-wide shadow-[0_10px_30px_-10px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all">
                              <Check className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                              Review
                            </button>
                          )}
                        </div>
                        {request.reviewed_by && (
                          <div className="mt-3 pt-3 border-t border-white/[0.05] flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              {request.status === 'approved' ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/[0.08] border border-emerald-500/25 text-emerald-300 text-[10px] tracking-wide">
                                  <CheckCircle className="w-3 h-3" /> Approved
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-500/[0.08] border border-rose-500/25 text-rose-300 text-[10px] tracking-wide">
                                  <XCircle className="w-3 h-3" /> Rejected
                                </span>
                              )}
                              <span className="text-white/50 text-xs">by {request.reviewed_by_name || request.reviewed_by}</span>
                            </div>
                            <span className="text-white/35 text-[10px] tracking-wider">{formatDate(request.reviewed_at)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ TEAM ROSTER TAB ═══ */}
      {viewMode === 'roster' && canViewAllShifts && (
        <div className="space-y-5">
          {/* Roster Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-base">Weekly Team Roster</h3>
                {weeklyRoster && (
                  <p className="text-white/40 text-xs">{new Date(weeklyRoster.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – {new Date(weeklyRoster.week_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                )}
              </div>
            </div>
            <Button onClick={() => setShowTemplateModal(true)} size="sm" data-testid="shift-templates-btn"
              className="bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white h-9 text-xs gap-1.5">
              <Settings className="w-3.5 h-3.5" /> Templates
            </Button>
          </div>

          {/* Today's Availability — Jewel */}
          <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md p-5">
            <div className="pointer-events-none absolute -top-16 right-1/4 w-60 h-40 bg-cyan-500/[0.08] rounded-full blur-[80px]" />
            <div className="relative flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-500/25 flex items-center justify-center">
                <Eye className="w-4 h-4 text-cyan-300" />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Live snapshot</p>
                <h3 className="text-white text-sm font-medium tracking-tight">Today's Availability</h3>
              </div>
            </div>
              {staffAvailability.length === 0 ? (
                <p className="text-white/40 text-xs italic text-center py-4">No staff data available</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                  {staffAvailability.map(s => {
                    const meta = s.status === 'on_shift'
                      ? { ring: 'border-emerald-500/25', bg: 'bg-emerald-500/5', dot: 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]', text: 'text-emerald-400' }
                      : s.status === 'on_leave'
                        ? { ring: 'border-red-500/25', bg: 'bg-red-500/5', dot: 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.6)]', text: 'text-red-400' }
                        : { ring: 'border-white/[0.08]', bg: 'bg-white/[0.02]', dot: 'bg-white/40', text: 'text-white/40' };
                    return (
                      <div key={s.username} className={`p-3 rounded-xl border ${meta.ring} ${meta.bg} transition-all hover:border-cyan-500/30`} data-testid={`availability-${s.username}`}>
                        <p className="text-white text-xs font-medium truncate">{s.display_name}</p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                          <span className={`text-[10px] capitalize ${meta.text}`}>{s.status.replace('_', ' ')}</span>
                        </div>
                        {s.shift && <p className="text-white/40 text-[10px] mt-1 tabular-nums">{s.shift.start_time} – {s.shift.end_time}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
          </div>

          {/* Weekly Roster Grid — Jewel */}
          {weeklyRoster && (
            <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md">
              <div className="pointer-events-none absolute -top-16 left-1/3 w-60 h-40 bg-violet-500/[0.08] rounded-full blur-[80px]" />
              <div className="relative px-5 py-4 flex items-center gap-3 border-b border-white/[0.05]">
                <div className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/25 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-violet-300" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Week ahead</p>
                  <h3 className="text-white text-sm font-medium tracking-tight">Weekly Schedule</h3>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.05] bg-white/[0.01]">
                      <th className="text-left p-3 text-white/40 text-[9px] font-semibold uppercase tracking-[0.2em] w-32">Day</th>
                      <th className="text-center p-3 text-white/40 text-[9px] font-semibold uppercase tracking-[0.2em]">Shifts</th>
                      <th className="text-center p-3 text-white/40 text-[9px] font-semibold uppercase tracking-[0.2em]">On Leave</th>
                      <th className="text-center p-3 text-white/40 text-[9px] font-semibold uppercase tracking-[0.2em]">Coverage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyRoster.days.map((day, i) => {
                      const isToday = new Date(day.date).toDateString() === new Date().toDateString();
                      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                      return (
                        <tr key={day.date} className={`border-b border-white/[0.04] transition-colors ${isToday ? 'bg-cyan-500/[0.06]' : 'hover:bg-white/[0.02]'}`}>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-semibold ${isToday ? 'text-cyan-300' : 'text-white'}`}>{dayNames[i]}</span>
                              <span className="text-white/40 text-[10px] tabular-nums">{new Date(day.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                              {isToday && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-[8px] font-semibold tracking-wider">TODAY</span>}
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-white text-sm font-medium tabular-nums">{day.coverage}</span>
                            {day.shifts.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5 justify-center">
                                {day.shifts.slice(0, 3).map((s, j) => (
                                  <span key={j} className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/[0.08] text-emerald-300 border border-emerald-500/25">
                                    {s.staff_display_name?.split(' ')[0] || s.staff_username}
                                  </span>
                                ))}
                                {day.shifts.length > 3 && <span className="text-[9px] text-white/40 self-center">+{day.shifts.length - 3}</span>}
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <span className={`text-sm font-medium tabular-nums ${day.on_leave > 0 ? 'text-rose-300' : 'text-white/30'}`}>{day.on_leave}</span>
                          </td>
                          <td className="p-3 text-center">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mx-auto text-sm font-bold border ${
                              day.coverage >= 3 ? 'bg-emerald-500/[0.10] text-emerald-300 border-emerald-500/30 shadow-[0_0_14px_-4px_rgba(52,211,153,0.4)]' :
                              day.coverage >= 1 ? 'bg-violet-500/[0.10] text-violet-300 border-violet-500/30' :
                              'bg-rose-500/[0.10] text-rose-300 border-rose-500/30 animate-pulse'
                            }`}>{day.coverage >= 3 ? '++' : day.coverage >= 1 ? '+' : '!'}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Weekly Hours Summary — Jewel */}
          {weeklyHours.length > 0 && (
            <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md">
              <div className="pointer-events-none absolute -top-16 right-1/4 w-60 h-40 bg-violet-500/[0.08] rounded-full blur-[80px]" />
              <div className="relative px-5 py-4 flex items-center gap-3 border-b border-white/[0.05]">
                <div className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/25 flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-violet-300" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Hours logged</p>
                  <h3 className="text-white text-sm font-medium tracking-tight">Weekly Hours Summary</h3>
                </div>
              </div>
              <div className="relative p-4 space-y-2">
                {weeklyHours.map(h => (
                  <div key={h.username} className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-violet-400/25 transition-all">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-violet-200">{(h.display_name || h.username)?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{h.display_name}</p>
                      <p className="text-white/40 text-[10px]">{h.shift_count} shifts</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm font-mono tabular-nums">{h.total_hours}<span className="text-white/40 text-[10px] ml-0.5">h</span></p>
                      {h.overtime_hours > 0 && <p className="text-rose-300 text-[10px] tracking-wide">+{h.overtime_hours}h OT</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Swap Requests — Jewel */}
          {swapRequests.length > 0 && (
            <div className="relative overflow-hidden rounded-3xl border border-pink-400/20 bg-gradient-to-br from-pink-900/[0.06] via-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md">
              <div className="pointer-events-none absolute -top-16 left-1/4 w-60 h-40 bg-pink-500/[0.10] rounded-full blur-[80px]" />
              <div className="relative px-5 py-4 flex items-center gap-3 border-b border-white/[0.05]">
                <div className="w-9 h-9 rounded-xl bg-pink-500/15 border border-pink-500/25 flex items-center justify-center">
                  <RotateCcw className="w-4 h-4 text-pink-300" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Requests</p>
                  <h3 className="text-white text-sm font-medium tracking-tight">Shift Swap Requests</h3>
                </div>
              </div>
              <div className="relative p-4 space-y-2">
                {swapRequests.map((swap, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-pink-500/[0.05] border border-pink-500/20 hover:border-pink-400/40 transition-all">
                    <div>
                      <p className="text-white text-sm"><span className="font-medium">{swap.requester_name}</span> <span className="text-white/40">→</span> <span className="font-medium text-pink-300">{swap.target_name}</span></p>
                      <p className="text-white/45 text-[11px] mt-0.5 tabular-nums">{swap.shift_date} · {swap.shift_time}</p>
                      {swap.reason && <p className="text-white/35 text-[10px] mt-0.5 italic">"{swap.reason}"</p>}
                    </div>
                    {swap.target === currentUser?.username && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-semibold shadow-[0_6px_18px_-6px_rgba(52,211,153,0.6)]"
                          onClick={async () => { const h = getAuthHeaders(); if(h) { await axios.post(`${BACKEND_URL}/api/shifts/swap-respond?shift_id=${swap.shift_id}&accept=true`, {}, { headers: h }); toast.success('Swap accepted'); fetchData(); } }}>
                          <Check className="w-3 h-3" /> Accept
                          </button>
                          <button
                            className="inline-flex items-center gap-1 w-8 h-7 justify-center rounded-full bg-rose-500/[0.08] border border-rose-500/25 text-rose-300 hover:bg-rose-500/[0.15] text-[10px] transition-all"
                            onClick={async () => { const h = getAuthHeaders(); if(h) { await axios.post(`${BACKEND_URL}/api/shifts/swap-respond?shift_id=${swap.shift_id}&accept=false`, {}, { headers: h }); toast.info('Swap declined'); fetchData(); } }}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
            </div>
          )}
        </div>
      )}

      {/* Shift Overview */}
      {viewMode === 'shifts' && canViewAllShifts && (
        <div className="space-y-6">
          {/* Month Range Selector */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/40">View range</span>
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.02] border border-white/[0.06]">
                {[1, 3, 6, 12].map(months => {
                  const a = shiftViewMonths === months;
                  return (
                    <button key={months} onClick={() => setShiftViewMonths(months)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide transition-all ${
                        a ? 'bg-gradient-to-r from-emerald-500/25 to-cyan-500/15 text-white border border-emerald-400/30 shadow-[0_0_14px_-4px_rgba(52,211,153,0.4)]'
                          : 'text-white/50 hover:text-white border border-transparent'
                      }`}>
                      {months === 1 ? 'This Month' : `${months} Months`}
                    </button>
                  );
                })}
              </div>
            </div>
            {isExecutive && (
              <button onClick={() => setShowShiftModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-xs font-semibold shadow-[0_10px_30px_-10px_rgba(52,211,153,0.6)] hover:-translate-y-0.5 transition-all">
                <Plus className="w-3.5 h-3.5" /> Add shift
              </button>
            )}
          </div>

          {/* Pending Shift Approvals — Jewel */}
          {isExecutive && pendingShifts.length > 0 && (
            <div className="relative overflow-hidden rounded-3xl border border-violet-400/20 bg-gradient-to-br from-violet-900/[0.10] via-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md">
              <div className="pointer-events-none absolute -top-24 right-1/3 w-72 h-72 rounded-full blur-[100px] opacity-30 bg-violet-500/25" />
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/50 to-transparent" />
              <div className="relative px-5 py-4 flex items-center justify-between flex-wrap gap-2 border-b border-white/[0.05]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500/25 to-pink-500/15 border border-violet-400/30 flex items-center justify-center shadow-[0_0_20px_-6px_rgba(138,43,226,0.5)]">
                    <Hourglass className="w-5 h-5 text-violet-200" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Executive review</p>
                    <h3 className="text-white text-base font-light tracking-tight">Shifts <span className="bg-gradient-to-r from-violet-300 to-pink-300 bg-clip-text text-transparent font-medium">awaiting approval</span></h3>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-500/[0.10] border border-violet-500/25 text-violet-300 text-[10px] tracking-wide animate-pulse">
                  <span className="w-1 h-1 rounded-full bg-violet-400" /> {pendingShifts.length} pending
                </span>
              </div>
              <div className="relative p-4 space-y-2.5">
                {pendingShifts.map(shift => (
                  <div key={shift.id} className="relative p-4 pl-5 rounded-2xl bg-gradient-to-br from-violet-500/[0.05] via-white/[0.02] to-transparent border border-violet-400/25 overflow-hidden hover:border-violet-400/40 transition-all">
                    <span className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-violet-400 to-pink-500" />
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/25 to-pink-500/15 border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-semibold">{(shift.staff_display_name || '?').charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{shift.staff_display_name}</p>
                          <p className="text-white/50 text-xs tabular-nums">
                            {formatDate(shift.date)} <span className="text-violet-300 mx-1">·</span> {shift.start_time} – {shift.end_time}
                            <span className="text-white/40 ml-1.5">({shift.hours_worked}h)</span>
                          </p>
                          <p className="text-white/35 text-[10px] mt-0.5">Requested by {shift.created_by_name || shift.created_by}</p>
                          {shift.notes && <p className="text-white/50 text-[11px] mt-1 italic">"{shift.notes}"</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openShiftApprovalModal(shift)}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-[11px] font-semibold tracking-wide shadow-[0_8px_20px_-6px_rgba(52,211,153,0.5)] transition-all">
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button onClick={() => { setSelectedShift(shift); handleApproveShift('rejected'); }}
                          className="w-8 h-8 rounded-full bg-rose-500/[0.08] border border-rose-500/25 text-rose-300 hover:bg-rose-500/[0.15] flex items-center justify-center transition-all">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats Cards — Jewel */}
          {shiftStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Total hours',    value: shiftStats.total_hours,    icon: Clock,     accent: 'emerald', hex: '#10B981' },
                { label: 'Regular hours',  value: shiftStats.regular_hours,  icon: Briefcase, accent: 'blue',    hex: '#3B82F6' },
                { label: 'Overtime hours', value: shiftStats.overtime_hours, icon: Timer,     accent: 'cyan',    hex: '#00E5FF' },
                { label: 'Total shifts',   value: shiftStats.total_shifts,   icon: BarChart3, accent: 'violet',  hex: '#8A2BE2' },
              ].map((s, i) => (
                <div key={i} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 hover:border-white/[0.15] transition-all group">
                  <div className="pointer-events-none absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-30 transition-opacity group-hover:opacity-60"
                    style={{ background: s.hex }} />
                  <div className="relative flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl border border-white/[0.08] flex items-center justify-center flex-shrink-0"
                      style={{ background: `${s.hex}20`, boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06), 0 0 20px -8px ${s.hex}` }}>
                      <s.icon className="w-4 h-4" style={{ color: s.hex }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-2xl font-light text-white tabular-nums leading-none">{s.value}</p>
                      <p className="text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1.5">{s.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Staff Breakdown — Jewel */}
          {shiftStats?.staff_breakdown && shiftStats.staff_breakdown.length > 0 && (
            <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0a14]/80 to-[#05050A]/80 backdrop-blur-md">
              <div className="pointer-events-none absolute -top-16 right-1/4 w-60 h-40 bg-cyan-500/[0.08] rounded-full blur-[80px]" />
              <div className="relative px-5 py-4 flex items-center gap-3 border-b border-white/[0.05]">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500/25 to-violet-500/15 border border-cyan-400/30 flex items-center justify-center">
                  <Users className="w-4 h-4 text-cyan-200" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Team breakdown</p>
                  <h3 className="text-white text-sm font-medium tracking-tight">Staff hours this month</h3>
                </div>
              </div>
              <div className="relative p-4 space-y-2">
                {shiftStats.staff_breakdown.map(staff => (
                  <div key={staff.username} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-400/25 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm font-semibold">{(staff.display_name || '?').charAt(0).toUpperCase()}</span>
                      </div>
                      <span className="text-white text-sm">{staff.display_name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-white/45 text-[11px] tabular-nums">{staff.total_shifts} shifts</span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-500/[0.08] border border-cyan-500/25 text-cyan-300 text-xs font-mono tabular-nums">{staff.total_hours}h</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Day Detail Modal */}
      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-light">
              <Calendar className="w-5 h-5 text-[#00E5FF]" />
              {selectedDayData?.displayDate}
            </DialogTitle>
          </DialogHeader>
          {selectedDayData && (
            <div className="space-y-3 mt-4">
              {/* UK Bank Holiday banner */}
              {selectedDayData.holiday && (() => {
                const hs = HOLIDAY_TONE_STYLES[selectedDayData.holiday.tone];
                return (
                  <div className={`relative overflow-hidden p-4 rounded-xl border ${hs.chipBorder} ${hs.cellBg}`} data-testid="uk-holiday-modal-banner">
                    <div className={`pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full blur-[60px] opacity-40 ${hs.chipBg}`} />
                    <div className="relative flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-xl ${hs.chipBg} ${hs.chipText} border ${hs.chipBorder} flex items-center justify-center text-xl`}>
                        {selectedDayData.holiday.emoji}
                      </div>
                      <div className="flex-1">
                        <p className={`text-[10px] tracking-[0.22em] uppercase font-medium ${hs.chipText}`}>UK Bank Holiday</p>
                        <p className="text-white text-sm font-medium mt-0.5">{selectedDayData.holiday.name}</p>
                        <p className="text-white/60 text-[11px] mt-1">All staff are automatically off — regular schedules are suspended.</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Paydays */}
              {selectedDayData.paydays?.length > 0 && (
                <div className="p-4 bg-[#F59E0B]/[0.06] border border-[#F59E0B]/25 rounded-xl">
                  <p className="text-[#fbbf24] font-medium flex items-center gap-2 text-sm">
                    <DollarSign className="w-4 h-4" /> Payday
                  </p>
                  {selectedDayData.paydays.map((p, i) => (
                    <p key={i} className="text-white/70 text-sm mt-1.5">{p.display_name}</p>
                  ))}
                </div>
              )}

              {/* Clock Records */}
              {selectedDayData.clockRecords?.length > 0 && (
                <div className="p-4 bg-[#00E5FF]/[0.05] border border-[#00E5FF]/25 rounded-xl">
                  <p className="text-[#00E5FF] font-medium flex items-center gap-2 text-sm mb-2">
                    <Clock className="w-4 h-4" /> Clock records
                  </p>
                  <div className="space-y-1.5">
                    {selectedDayData.clockRecords.map((r, i) => (
                      <div key={i} className="text-white/70 text-sm flex justify-between items-center bg-white/[0.02] rounded-lg px-3 py-2 border border-white/[0.04]">
                        <span className="font-mono">{r.clock_in_time?.split('T')[1]?.slice(0,5)} — {r.clock_out_time?.split('T')[1]?.slice(0,5) || 'Active'}</span>
                        <span className="text-[#00E5FF] font-medium">{Math.floor(r.total_minutes / 60)}h {r.total_minutes % 60}m</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Regular Shifts */}
              {selectedDayData.regularShifts?.length > 0 && (
                <div className="p-4 bg-[#8A2BE2]/[0.06] border border-[#8A2BE2]/25 rounded-xl">
                  <p className="text-[#c9a4f0] font-medium flex items-center gap-2 text-sm mb-2">
                    <Calendar className="w-4 h-4" /> Regular schedules
                  </p>
                  <div className="space-y-1.5">
                    {selectedDayData.regularShifts.map((s, i) => (
                      <div key={i} className="text-white/70 text-sm flex justify-between items-center bg-white/[0.02] rounded-lg px-3 py-2 border border-white/[0.04]">
                        <span>{s.display_name}</span>
                        <span className="font-mono text-[#c9a4f0]">{s.start_time} — {s.end_time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shifts */}
              {selectedDayData.shifts?.length > 0 && (
                <div className="p-4 bg-emerald-500/[0.06] border border-emerald-500/25 rounded-xl">
                  <p className="text-emerald-300 font-medium flex items-center gap-2 text-sm mb-2">
                    <Timer className="w-4 h-4" /> Assigned shifts
                  </p>
                  <div className="space-y-1.5">
                    {selectedDayData.shifts.map((s, i) => (
                      <div key={i} className="text-white/70 text-sm flex justify-between items-center bg-white/[0.02] rounded-lg px-3 py-2 border border-white/[0.04]">
                        <span>{s.staff_display_name}</span>
                        <span className="font-mono text-emerald-300">{s.start_time} — {s.end_time} <span className="text-white/40">({s.hours_worked}h)</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leave */}
              {selectedDayData.leaves?.length > 0 && (
                <div className="p-4 bg-blue-500/[0.06] border border-blue-500/25 rounded-xl">
                  <p className="text-blue-300 font-medium flex items-center gap-2 text-sm mb-2">
                    <Briefcase className="w-4 h-4" /> Leave
                  </p>
                  <div className="space-y-1.5">
                    {selectedDayData.leaves.map((l, i) => (
                      <div key={i} className="text-white/70 text-sm flex justify-between items-center bg-white/[0.02] rounded-lg px-3 py-2 border border-white/[0.04]">
                        <span>{l.requested_by_name}</span>
                        <Badge className={getStatusBadge(l.status)}>{l.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No events */}
              {!selectedDayData.paydays?.length && !selectedDayData.clockRecords?.length && !selectedDayData.shifts?.length && !selectedDayData.leaves?.length && !selectedDayData.regularShifts?.length && !selectedDayData.holiday && (
                <div className="p-8 rounded-xl bg-white/[0.02] border border-dashed border-white/[0.08] text-center">
                  <Calendar className="w-8 h-8 mx-auto text-white/20 mb-2" />
                  <p className="text-white/40 text-sm">Nothing scheduled on this day.</p>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setShowDayModal(false)} className="bg-white/[0.02] border-white/10 text-white/80 hover:bg-white/[0.05] rounded-full">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Leave Request Modal - REVAMPED */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="bg-[#0a0a14] border-cyan-500/25 text-white max-w-xl p-0 overflow-hidden">
          {/* Ambient glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-cyan-500/[0.08] rounded-full blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 bg-violet-500/[0.06] rounded-full blur-3xl" />

          <div className="relative p-6 border-b border-white/[0.06] bg-gradient-to-br from-cyan-500/[0.06] to-violet-500/[0.04]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="relative w-11 h-11 flex-shrink-0">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Palmtree className="w-5 h-5 text-white" strokeWidth={1.8} />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-cyan-300/70 mb-0.5">Time Off Request</p>
                  <span className="text-xl font-semibold text-white leading-none">Request Time Off</span>
                </div>
              </DialogTitle>
            </DialogHeader>
            {myAllowance && (
              <div className="mt-4 flex items-center gap-3 text-[11px]">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/25">
                  <TrendingUp className="w-3 h-3 text-cyan-300" />
                  <span className="text-cyan-200 font-semibold">{myAllowance.remaining_hours}h</span>
                  <span className="text-white/50">available</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-violet-500/10 border border-violet-500/25">
                  <Check className="w-3 h-3 text-violet-300" />
                  <span className="text-violet-200 font-semibold">{myAllowance.used_hours}h</span>
                  <span className="text-white/50">used</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/[0.03] border border-white/10">
                  <span className="text-white/70 font-semibold">{myAllowance.total_hours + myAllowance.extra_hours}h</span>
                  <span className="text-white/40">total</span>
                </div>
              </div>
            )}
          </div>

          <div className="relative p-6 space-y-5 max-h-[65vh] overflow-y-auto">
            {/* Leave Type — Rich Cards */}
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2.5 block font-medium">What type of leave?</label>
              <div className="grid grid-cols-5 gap-2">
                {LEAVE_TYPES.map(type => {
                  const active = formData.leave_type === type.id;
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, leave_type: type.id })}
                      data-testid={`leave-type-${type.id}`}
                      className={`group relative overflow-hidden p-3 rounded-xl border transition-all ${
                        active
                          ? `bg-gradient-to-br ${type.grad} border-white/30 text-white shadow-lg`
                          : 'bg-white/[0.02] border-white/[0.08] hover:bg-white/[0.05] hover:border-white/20'
                      }`}
                    >
                      {active && <div className="absolute inset-0 bg-black/10" />}
                      <div className="relative flex flex-col items-center gap-1.5">
                        <Icon className={`w-4 h-4 ${active ? 'text-white' : `text-${type.accent}-400`}`} strokeWidth={active ? 2 : 1.5} />
                        <span className={`text-[11px] font-semibold ${active ? 'text-white' : 'text-white/70'}`}>{type.short}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick Duration Presets */}
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2.5 block font-medium">Quick select</label>
              <div className="flex flex-wrap gap-2">
                {LEAVE_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => {
                      const start = new Date();
                      start.setDate(start.getDate() + (preset.id === 'today' ? 0 : preset.id === 'tomorrow' ? 1 : 0));
                      const end = new Date(start);
                      if (preset.days > 0) end.setDate(end.getDate() + preset.days - 1);
                      const iso = d => d.toISOString().split('T')[0];
                      setFormData({ ...formData, start_date: iso(start), end_date: iso(end) });
                    }}
                    data-testid={`leave-preset-${preset.id}`}
                    className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 hover:border-cyan-500/40 hover:bg-cyan-500/[0.06] text-[11px] text-white/70 hover:text-cyan-200 transition-all"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2 block font-medium">From</label>
                <div className="relative">
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20 focus:bg-white/[0.05] outline-none transition-all"
                  />
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400/70 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2 block font-medium">To</label>
                <div className="relative">
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:border-violet-500/60 focus:ring-2 focus:ring-violet-500/20 focus:bg-white/[0.05] outline-none transition-all"
                  />
                  <ArrowRight className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-violet-400/70 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Duration Preview */}
            {formData.start_date && formData.end_date && (() => {
              const start = new Date(formData.start_date);
              const end = new Date(formData.end_date);
              const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
              const hours = days * 8;
              const overBudget = myAllowance && hours > myAllowance.remaining_hours;
              return (
                <div className={`relative overflow-hidden rounded-xl p-3.5 border ${overBudget ? 'bg-rose-500/[0.06] border-rose-500/30' : 'bg-gradient-to-r from-cyan-500/[0.06] to-violet-500/[0.06] border-cyan-500/20'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg ${overBudget ? 'bg-rose-500/15' : 'bg-cyan-500/15'} flex items-center justify-center`}>
                        <Clock className={`w-4 h-4 ${overBudget ? 'text-rose-300' : 'text-cyan-300'}`} />
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/50">Total duration</p>
                        <p className={`text-sm font-semibold ${overBudget ? 'text-rose-200' : 'text-white'}`}>
                          {days} day{days > 1 ? 's' : ''} · {hours}h
                        </p>
                      </div>
                    </div>
                    {overBudget && (
                      <div className="flex items-center gap-1.5 text-[11px] text-rose-300">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Exceeds balance
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Reason */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-medium">Reason</label>
                <span className={`text-[10px] ${(formData.reason || '').length > 300 ? 'text-rose-400' : 'text-white/40'}`}>
                  {(formData.reason || '').length}/500
                </span>
              </div>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value.slice(0, 500) })}
                placeholder="A brief explanation helps managers approve faster…"
                rows={3}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3.5 py-3 text-white text-sm placeholder:text-white/30 focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20 focus:bg-white/[0.05] outline-none resize-none transition-all"
              />
            </div>
          </div>

          {/* Footer actions */}
          <div className="relative flex gap-2 p-4 border-t border-white/[0.06] bg-black/40">
            <Button
              variant="ghost"
              onClick={() => setShowRequestModal(false)}
              className="flex-1 h-11 text-white/50 hover:text-white hover:bg-white/[0.05] border border-transparent hover:border-white/10 rounded-xl transition-all"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRequest}
              disabled={!formData.start_date || !formData.end_date}
              data-testid="submit-leave-request-btn"
              className="group flex-1 h-11 relative overflow-hidden bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-cyan-500/25 rounded-xl transition-all"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Check className="w-4 h-4 mr-1.5" />
              Submit Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Review Modal — refreshed */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="bg-[#0a0a14] border-violet-500/25 text-white max-w-md p-0 overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 bg-violet-500/[0.08] rounded-full blur-3xl" />
          <div className="relative p-5 border-b border-white/[0.06] bg-gradient-to-br from-violet-500/[0.06] to-transparent">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-violet-300/70 mb-0.5">Approval Queue</p>
                  <span className="text-lg font-semibold text-white leading-none">Review Request</span>
                </div>
              </DialogTitle>
            </DialogHeader>
          </div>
          {selectedRequest && (
            <div className="relative p-5 space-y-4">
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.08]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center text-cyan-300 text-xs font-semibold">
                    {(selectedRequest.requested_by_name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm leading-none">{selectedRequest.requested_by_name}</p>
                    <p className="text-white/40 text-[11px] mt-0.5">{formatDate(selectedRequest.start_date)} → {formatDate(selectedRequest.end_date)}</p>
                  </div>
                </div>
                {selectedRequest.reason && (
                  <p className="text-white/70 text-[13px] leading-relaxed mt-3 pt-3 border-t border-white/[0.06]">{selectedRequest.reason}</p>
                )}
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2 block font-medium">Notes (optional)</label>
                <textarea
                  value={reviewData.review_notes}
                  onChange={(e) => setReviewData({ ...reviewData, review_notes: e.target.value })}
                  placeholder="Add context for the requester…"
                  rows={2}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder:text-white/30 focus:border-violet-500/60 focus:ring-2 focus:ring-violet-500/20 outline-none resize-none transition-all"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="ghost"
                  onClick={() => setShowReviewModal(false)}
                  className="flex-1 h-10 text-white/50 hover:text-white hover:bg-white/[0.05] border border-transparent hover:border-white/10 rounded-xl transition-all"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleReview('rejected')}
                  data-testid="reject-leave-btn"
                  className="flex-1 h-10 bg-rose-500/15 hover:bg-rose-500/25 text-rose-200 border border-rose-500/30 hover:border-rose-500/50 rounded-xl transition-all"
                >
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button
                  onClick={() => handleReview('approved')}
                  data-testid="approve-leave-btn"
                  className="flex-1 h-10 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white shadow-lg shadow-emerald-500/25 hover:-translate-y-0.5 rounded-xl transition-all"
                >
                  <Check className="w-4 h-4 mr-1" /> Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Shift Modal — REVAMPED */}
      <Dialog open={showShiftModal} onOpenChange={setShowShiftModal}>
        <DialogContent className="bg-[#0a0a14] border-emerald-500/25 text-white max-w-xl p-0 overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-emerald-500/[0.08] rounded-full blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 bg-cyan-500/[0.06] rounded-full blur-3xl" />

          <div className="relative p-6 border-b border-white/[0.06] bg-gradient-to-br from-emerald-500/[0.06] to-cyan-500/[0.04]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="relative w-11 h-11 flex-shrink-0">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 shadow-lg shadow-emerald-500/30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Timer className="w-5 h-5 text-white" strokeWidth={1.8} />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-emerald-300/70 mb-0.5">Workforce Schedule</p>
                  <span className="text-xl font-semibold text-white leading-none">
                    {isManager && !isExecutive ? 'Request Shift' : 'Add Shift'}
                  </span>
                </div>
              </DialogTitle>
            </DialogHeader>
            {isManager && !isExecutive && (
              <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/[0.08] border border-cyan-500/25">
                <Info className="w-3.5 h-3.5 text-cyan-300 flex-shrink-0" />
                <p className="text-cyan-200 text-[11px]">Requires executive approval before it goes live.</p>
              </div>
            )}
          </div>

          <div className="relative p-6 space-y-5 max-h-[65vh] overflow-y-auto">
            {/* Staff Selection with avatars */}
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2.5 block font-medium">Assign to</label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                {staffList.map(staff => {
                  const active = shiftFormData.staff_username === staff.username;
                  const initials = (staff.display_name || staff.username).split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                  return (
                    <button
                      key={staff.username}
                      type="button"
                      onClick={() => setShiftFormData({ ...shiftFormData, staff_username: staff.username })}
                      className={`group relative flex items-center gap-2.5 p-2.5 rounded-xl border text-left transition-all ${
                        active
                          ? 'bg-emerald-500/[0.12] border-emerald-500/40 shadow-[0_0_16px_rgba(52,211,153,0.15)]'
                          : 'bg-white/[0.02] border-white/[0.08] hover:bg-white/[0.05] hover:border-white/20'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${
                        active ? 'bg-emerald-500 text-white' : 'bg-white/[0.05] text-white/70'
                      }`}>
                        {initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-[12px] font-medium truncate ${active ? 'text-white' : 'text-white/70'}`}>
                          {staff.display_name || staff.username}
                        </p>
                        <p className="text-[10px] text-white/40 truncate capitalize">{staff.role}</p>
                      </div>
                      {active && <Check className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Shift Presets */}
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2.5 block font-medium">Quick shift</label>
              <div className="grid grid-cols-5 gap-2">
                {SHIFT_PRESETS.map(preset => {
                  const active = shiftFormData.start_time === preset.start && shiftFormData.end_time === preset.end;
                  const Icon = preset.icon;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => setShiftFormData({ ...shiftFormData, start_time: preset.start, end_time: preset.end })}
                      className={`p-2.5 rounded-xl border transition-all text-center ${
                        active
                          ? 'bg-emerald-500/[0.12] border-emerald-500/40 text-emerald-200'
                          : 'bg-white/[0.02] border-white/[0.08] hover:border-white/20 text-white/70 hover:text-white'
                      }`}
                    >
                      <Icon className={`w-4 h-4 mx-auto mb-1 ${active ? 'text-emerald-300' : 'text-white/40'}`} />
                      <p className="text-[10px] font-medium">{preset.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2 block font-medium">Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={shiftFormData.date}
                    onChange={(e) => setShiftFormData({ ...shiftFormData, date: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                  />
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400/70 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2 block font-medium">Start</label>
                <div className="relative">
                  <input
                    type="time"
                    value={shiftFormData.start_time}
                    onChange={(e) => setShiftFormData({ ...shiftFormData, start_time: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all"
                  />
                  <Sunrise className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400/70 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2 block font-medium">End</label>
                <div className="relative">
                  <input
                    type="time"
                    value={shiftFormData.end_time}
                    onChange={(e) => setShiftFormData({ ...shiftFormData, end_time: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20 outline-none transition-all"
                  />
                  <Sunset className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400/70 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Duration preview */}
            {shiftFormData.start_time && shiftFormData.end_time && (() => {
              const [sh, sm] = shiftFormData.start_time.split(':').map(Number);
              const [eh, em] = shiftFormData.end_time.split(':').map(Number);
              let hours = eh - sh + (em - sm) / 60;
              if (hours < 0) hours += 24; // overnight
              return (
                <div className="rounded-xl p-3.5 border bg-gradient-to-r from-emerald-500/[0.06] to-cyan-500/[0.06] border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-emerald-300" />
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-white/50">Shift duration</p>
                        <p className="text-sm font-semibold text-white">{hours.toFixed(1)}h</p>
                      </div>
                    </div>
                    {hours >= 8 && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300">Full-length</span>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Shift type — rich cards with icons */}
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2.5 block font-medium">Shift type</label>
              <div className="grid grid-cols-4 gap-2">
                {SHIFT_TYPES.map(type => {
                  const active = shiftFormData.shift_type === type.id;
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setShiftFormData({ ...shiftFormData, shift_type: type.id })}
                      data-testid={`shift-type-${type.id}`}
                      className={`p-3 rounded-xl border transition-all ${
                        active
                          ? `bg-gradient-to-br ${type.grad} border-white/30 text-white shadow-lg`
                          : 'bg-white/[0.02] border-white/[0.08] hover:bg-white/[0.05] hover:border-white/20'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1.5">
                        <Icon className={`w-4 h-4 ${active ? 'text-white' : `text-${type.accent}-400`}`} strokeWidth={active ? 2 : 1.5} />
                        <span className={`text-[11px] font-semibold ${active ? 'text-white' : 'text-white/70'}`}>{type.short}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-2 block font-medium">Notes (optional)</label>
              <textarea
                value={shiftFormData.notes}
                onChange={(e) => setShiftFormData({ ...shiftFormData, notes: e.target.value })}
                placeholder="Special instructions, break times, coverage notes…"
                rows={2}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3.5 py-3 text-white text-sm placeholder:text-white/30 focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none transition-all"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="relative flex gap-2 p-4 border-t border-white/[0.06] bg-black/40">
            <Button
              variant="ghost"
              onClick={() => setShowShiftModal(false)}
              className="flex-1 h-11 text-white/50 hover:text-white hover:bg-white/[0.05] border border-transparent hover:border-white/10 rounded-xl transition-all"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitShift}
              disabled={!shiftFormData.staff_username || !shiftFormData.date || !shiftFormData.start_time || !shiftFormData.end_time}
              data-testid="submit-shift-btn"
              className="group flex-1 h-11 relative overflow-hidden bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-400 hover:to-cyan-500 text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 rounded-xl transition-all"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Check className="w-4 h-4 mr-1.5" />
              {isManager && !isExecutive ? 'Submit Request' : 'Add Shift'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shift Approval Modal (Executive) */}
      <Dialog open={showShiftApprovalModal} onOpenChange={setShowShiftApprovalModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              Approve Shift
            </DialogTitle>
          </DialogHeader>
          {selectedShift && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-black/40 rounded-xl">
                <p className="text-white font-medium">{selectedShift.staff_display_name}</p>
                <p className="text-white/50 text-sm">{formatDate(selectedShift.date)}</p>
                <p className="text-white/50 text-sm">{selectedShift.start_time} - {selectedShift.end_time} ({selectedShift.hours_worked}h)</p>
                <p className="text-white/40 text-xs mt-2">Requested by: {selectedShift.created_by_name}</p>
                {selectedShift.notes && <p className="text-white/40 text-xs">Notes: {selectedShift.notes}</p>}
              </div>
              <div>
                <label className="text-sm text-white/50 mb-1 block">Approval Notes (optional)</label>
                <textarea
                  value={shiftApprovalData.approval_notes}
                  onChange={(e) => setShiftApprovalData({ ...shiftApprovalData, approval_notes: e.target.value })}
                  placeholder="Add notes..."
                  rows={2}
                  className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowShiftApprovalModal(false)}>Cancel</Button>
                <Button onClick={() => handleApproveShift('rejected')} className="bg-red-500 hover:bg-red-600">
                  <X className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button onClick={() => handleApproveShift('approved')} className="bg-green-500 hover:bg-green-600">
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Status Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-400" />
              Request Details
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-center mb-4">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                  selectedRequest.status === 'approved' ? 'bg-green-500/20' :
                  selectedRequest.status === 'rejected' ? 'bg-red-500/20' :
                  selectedRequest.status === 'withdrawn' ? 'bg-white/[0.04]' :
                  'bg-cyan-500/20'
                }`}>
                  {selectedRequest.status === 'approved' && <CheckCircle className="w-10 h-10 text-green-400" />}
                  {selectedRequest.status === 'rejected' && <XCircle className="w-10 h-10 text-red-400" />}
                  {selectedRequest.status === 'withdrawn' && <RotateCcw className="w-10 h-10 text-white/50" />}
                  {selectedRequest.status === 'pending' && <Hourglass className="w-10 h-10 text-cyan-400" />}
                </div>
              </div>
              <div className="text-center">
                <Badge className={getStatusBadge(selectedRequest.status) + ' text-lg px-4 py-1'}>
                  {selectedRequest.status.toUpperCase()}
                </Badge>
              </div>
              <div className="p-4 bg-black/40 rounded-xl space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/50">Type:</span>
                  <span className="text-white">{getLeaveTypeInfo(selectedRequest.leave_type).label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">From:</span>
                  <span className="text-white">{formatDate(selectedRequest.start_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">To:</span>
                  <span className="text-white">{formatDate(selectedRequest.end_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Submitted:</span>
                  <span className="text-white">{formatDate(selectedRequest.created_at)}</span>
                </div>
              </div>
              <div className="p-4 bg-black/40 rounded-xl">
                <p className="text-white/50 text-sm mb-1">Reason:</p>
                <p className="text-white">{selectedRequest.reason}</p>
              </div>
              {selectedRequest.reviewed_by && (
                <div className="p-4 bg-black/40 rounded-xl">
                  <p className="text-white/50 text-sm mb-1">Reviewed by:</p>
                  <p className="text-white">{selectedRequest.reviewed_by_name || selectedRequest.reviewed_by}</p>
                  {selectedRequest.review_notes && (
                    <>
                      <p className="text-white/50 text-sm mt-2 mb-1">Notes:</p>
                      <p className="text-white">{selectedRequest.review_notes}</p>
                    </>
                  )}
                </div>
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowStatusModal(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Workforce Settings — Jewel Command Centre */}
      <JewelModal
        open={showWorkforceModal}
        onOpenChange={setShowWorkforceModal}
        icon={Settings}
        title="Workforce Command"
        subtitle="Schedules · paydays · leave · live analytics"
        accent="violet"
        size="4xl"
        testid="workforce-settings-modal"
      >
        {/* Jewel Tab Rail (4 tabs — Analytics is new) */}
        <div className="mb-5 inline-flex items-center gap-1 p-1.5 bg-white/[0.02] backdrop-blur-md rounded-2xl border border-white/[0.06] w-full flex-wrap">
          {[
            { id: 'analytics', label: 'Analytics', icon: BarChart3, accent: 'cyan', hex: '#00E5FF' },
            { id: 'schedules', label: 'Schedules', icon: Calendar,  accent: 'emerald', hex: '#10B981' },
            { id: 'payday',    label: 'Paydays',   icon: DollarSign, accent: 'amber',   hex: '#F59E0B' },
            { id: 'leave',     label: 'Leave',     icon: Briefcase,  accent: 'violet',  hex: '#8A2BE2' },
          ].map(tab => {
            const a = workforceTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setWorkforceTab(tab.id)}
                data-testid={`workforce-tab-${tab.id}`}
                className={`flex-1 min-w-[110px] group relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium tracking-wide transition-all overflow-hidden ${
                  a ? 'text-white border border-white/20' : 'text-white/50 hover:text-white hover:bg-white/[0.03] border border-transparent'
                }`}
                style={a ? { background: `linear-gradient(135deg, ${tab.hex}22, ${tab.hex}0a)`, boxShadow: `0 0 22px -8px ${tab.hex}, inset 0 1px 0 rgba(255,255,255,0.05)` } : {}}>
                {a && <span className={`absolute inset-x-3 -bottom-px h-[2px]`} style={{ background: `linear-gradient(90deg, transparent, ${tab.hex}, transparent)` }} />}
                <tab.icon className={`w-3.5 h-3.5 transition-transform ${a ? 'scale-110' : 'group-hover:scale-105'}`} style={a ? { color: tab.hex } : {}} strokeWidth={a ? 2 : 1.5} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="max-h-[60vh] overflow-y-auto pr-2 jewel-scroll">
          {/* NEW · Analytics Tab */}
          {workforceTab === 'analytics' && (
            <div className="space-y-4">
              {(() => {
                const totalStaff = allLeaveConfigs.length || staffList.length;
                const totalBase = allLeaveConfigs.reduce((sum, c) => sum + (c.total_hours || 50) + (c.extra_hours || 0), 0);
                const totalUsed = allLeaveConfigs.reduce((sum, c) => sum + (c.used_hours || 0), 0);
                const totalWorked = allLeaveConfigs.reduce((sum, c) => sum + (c.worked_hours || 0), 0);
                const usagePct = totalBase > 0 ? Math.round((totalUsed / totalBase) * 100) : 0;
                const scheduledStaff = allSchedules.filter(s => s.schedules?.some(x => x.is_active)).length;
                const paydayConfigured = paydayConfigs.filter(p => p.payday_type).length;
                const topBurners = [...allLeaveConfigs].sort((a, b) => (b.used_hours || 0) - (a.used_hours || 0)).slice(0, 3);
                return (
                  <>
                    {/* KPI grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Team size',           value: totalStaff,          icon: Users,     hex: '#00E5FF' },
                        { label: 'Leave used',          value: `${usagePct}%`,      icon: TrendingUp, hex: '#8A2BE2' },
                        { label: 'Hours logged',        value: `${totalWorked}h`,   icon: Clock,     hex: '#10B981' },
                        { label: 'Rota completeness',   value: `${totalStaff > 0 ? Math.round((scheduledStaff / totalStaff) * 100) : 0}%`, icon: Calendar,  hex: '#F59E0B' },
                      ].map((k, i) => (
                        <div key={i} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 group hover:border-white/[0.15] transition-all">
                          <div className="pointer-events-none absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ background: k.hex }} />
                          <div className="relative flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl border border-white/[0.08] flex items-center justify-center"
                              style={{ background: `${k.hex}20`, boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06), 0 0 18px -8px ${k.hex}` }}>
                              <k.icon className="w-4 h-4" style={{ color: k.hex }} strokeWidth={1.5} />
                            </div>
                            <div>
                              <p className="text-2xl font-light text-white tabular-nums leading-none">{k.value}</p>
                              <p className="text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1.5">{k.label}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Coverage + Payday health */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Payday coverage</p>
                          <span className="text-white/70 text-xs font-mono">{paydayConfigured}/{staffList.length}</span>
                        </div>
                        <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
                            style={{ width: `${staffList.length > 0 ? (paydayConfigured / staffList.length) * 100 : 0}%` }} />
                        </div>
                        <p className="text-white/40 text-[11px] mt-2">Staff missing a payday cadence show up unlabelled on the calendar. Configure them from the Paydays tab.</p>
                      </div>

                      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Rota completeness</p>
                          <span className="text-white/70 text-xs font-mono">{scheduledStaff}/{totalStaff}</span>
                        </div>
                        <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all"
                            style={{ width: `${totalStaff > 0 ? (scheduledStaff / totalStaff) * 100 : 0}%` }} />
                        </div>
                        <p className="text-white/40 text-[11px] mt-2">Staff with at least one active weekday. Fill gaps from the Schedules tab.</p>
                      </div>
                    </div>

                    {/* Top leave burners */}
                    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-violet-500/15 border border-violet-500/25 flex items-center justify-center">
                          <TrendingUp className="w-3.5 h-3.5 text-violet-300" />
                        </div>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Top leave usage · this year</p>
                      </div>
                      {topBurners.length === 0 ? (
                        <p className="text-white/40 text-xs italic py-4 text-center">No leave used yet</p>
                      ) : (
                        <div className="space-y-2">
                          {topBurners.map((c, i) => {
                            const total = (c.total_hours || 50) + (c.extra_hours || 0);
                            const pct = total > 0 ? Math.round(((c.used_hours || 0) / total) * 100) : 0;
                            return (
                              <div key={c.username} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                                <span className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500/30 to-cyan-500/20 border border-white/[0.08] flex items-center justify-center text-xs font-semibold text-white">{i + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm truncate">{c.display_name}</p>
                                  <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden mt-1.5">
                                    <div className="h-full bg-gradient-to-r from-violet-400 to-pink-500" style={{ width: `${Math.min(100, pct)}%` }} />
                                  </div>
                                </div>
                                <span className="text-violet-300 text-xs font-mono tabular-nums">{c.used_hours || 0}h · {pct}%</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Quick actions */}
                    <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-cyan-500/[0.04] to-violet-500/[0.03] p-4">
                      <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">Quick actions</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <button onClick={() => setWorkforceTab('schedules')}
                          className="p-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20 text-emerald-300 hover:bg-emerald-500/[0.12] transition-all text-left">
                          <Calendar className="w-4 h-4 mb-1.5" />
                          <p className="text-xs font-medium">Fill rota gaps</p>
                          <p className="text-[10px] text-white/40 mt-0.5">Assign weekly hours</p>
                        </button>
                        <button onClick={() => setWorkforceTab('payday')}
                          className="p-3 rounded-xl bg-amber-500/[0.06] border border-amber-500/20 text-amber-300 hover:bg-amber-500/[0.12] transition-all text-left">
                          <DollarSign className="w-4 h-4 mb-1.5" />
                          <p className="text-xs font-medium">Set payday cadence</p>
                          <p className="text-[10px] text-white/40 mt-0.5">Monthly · weekly · bi-weekly</p>
                        </button>
                        <button onClick={() => openAddHoursModal()}
                          className="p-3 rounded-xl bg-violet-500/[0.06] border border-violet-500/20 text-violet-300 hover:bg-violet-500/[0.12] transition-all text-left">
                          <Plus className="w-4 h-4 mb-1.5" />
                          <p className="text-xs font-medium">Bonus / deduction</p>
                          <p className="text-[10px] text-white/40 mt-0.5">Adjust leave hours</p>
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          {/* Schedules Tab */}
          {workforceTab === 'schedules' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <p className="text-white/50 text-xs">Set regular working hours for each staff member</p>
                <button onClick={() => openConfigModal('schedule')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-[11px] font-semibold shadow-[0_8px_20px_-6px_rgba(52,211,153,0.5)] transition-all">
                  <Plus className="w-3.5 h-3.5" /> Add schedule
                </button>
              </div>
              {allSchedules.map(staff => (
                <div key={staff.username} className="relative overflow-hidden p-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-emerald-400/25 transition-all">
                  <div className="pointer-events-none absolute -top-16 right-1/4 w-48 h-32 bg-emerald-500/[0.06] rounded-full blur-[70px]" />
                  <div className="relative flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/25 to-teal-500/15 border border-emerald-400/30 flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">{(staff.display_name || '?').charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{staff.display_name}</p>
                        <p className="text-white/40 text-[10px] tracking-wide">{staff.schedules?.filter(s => s.is_active).length || 0} days scheduled</p>
                      </div>
                    </div>
                    <button onClick={() => openConfigModal('schedule', staff)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500/[0.08] border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/[0.15] text-[11px] font-medium transition-all">
                      <Plus className="w-3 h-3" /> Add day
                    </button>
                  </div>
                  <div className="relative grid grid-cols-7 gap-2">
                    {DAYS_OF_WEEK.map(day => {
                      const schedule = staff.schedules?.find(s => s.day_of_week === day.id);
                      return (
                        <div key={day.id} className={`p-2.5 rounded-xl text-center transition-all border ${
                          schedule?.is_active
                            ? 'bg-emerald-500/[0.10] border-emerald-400/30 shadow-[0_0_14px_-6px_rgba(52,211,153,0.4)]'
                            : 'bg-white/[0.02] border-white/[0.05]'
                        }`}>
                          <p className={`text-[10px] font-semibold tracking-wider mb-2 ${schedule?.is_active ? 'text-emerald-300' : 'text-white/40'}`}>{day.short}</p>
                          {schedule?.is_active ? (
                            <div className="space-y-0.5">
                              <p className="text-white text-xs font-medium tabular-nums">{schedule.start_time}</p>
                              <p className="text-white/30 text-[10px]">to</p>
                              <p className="text-white text-xs font-medium tabular-nums">{schedule.end_time}</p>
                              <button onClick={() => handleDeleteSchedule(staff.username, day.id)}
                                className="mt-1.5 w-full py-0.5 rounded-md bg-rose-500/[0.08] border border-rose-500/20 text-rose-300 hover:bg-rose-500/[0.15] text-[9px] transition-all">
                                Remove
                              </button>
                            </div>
                          ) : (
                            <p className="text-white/25 text-[10px] py-3 italic">Off</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Payday Tab */}
          {workforceTab === 'payday' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <p className="text-white/50 text-xs">Configure when each staff member gets paid</p>
                <button onClick={() => openConfigModal('payday')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white text-[11px] font-semibold shadow-[0_8px_20px_-6px_rgba(251,191,36,0.5)] transition-all">
                  <Plus className="w-3.5 h-3.5" /> Configure payday
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {staffList.map(staff => {
                  const config = paydayConfigs.find(p => p.username === staff.username);
                  const configured = !!config?.payday_type;
                  return (
                    <div key={staff.username} className={`relative overflow-hidden p-4 rounded-2xl border transition-all ${
                      configured ? 'border-amber-400/25 bg-gradient-to-br from-amber-500/[0.05] to-transparent hover:border-amber-400/40' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15]'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${configured ? 'bg-amber-500/20 border-amber-400/30' : 'bg-white/[0.02] border-white/[0.08]'}`}>
                            <DollarSign className={`w-4 h-4 ${configured ? 'text-amber-300' : 'text-white/30'}`} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{staff.display_name}</p>
                            {configured ? (
                              <p className="text-amber-300 text-[11px] tracking-wide">
                                {config.payday_type === 'monthly' && `Monthly · day ${config.payday_day}`}
                                {config.payday_type === 'weekly' && `Weekly · ${DAYS_OF_WEEK[config.payday_day]?.name}`}
                                {config.payday_type === 'biweekly' && `Bi-weekly · ${DAYS_OF_WEEK[config.payday_day]?.name}`}
                              </p>
                            ) : (
                              <p className="text-white/40 text-[11px] italic">Not configured</p>
                            )}
                          </div>
                        </div>
                        <button onClick={() => openConfigModal('payday', staff)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${configured ? 'bg-amber-500/[0.10] border border-amber-500/25 text-amber-300 hover:bg-amber-500/[0.20]' : 'bg-white/[0.04] border border-white/[0.08] text-white/50 hover:text-white'}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Leave Allowances Tab */}
          {workforceTab === 'leave' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <p className="text-white/50 text-xs">Manage leave allowances and add bonus hours</p>
                <button onClick={() => openAddHoursModal()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 hover:from-violet-400 hover:to-pink-400 text-white text-[11px] font-semibold shadow-[0_8px_20px_-6px_rgba(138,43,226,0.5)] transition-all">
                  <Plus className="w-3.5 h-3.5" /> Adjust hours
                </button>
              </div>
              {allLeaveConfigs.map(config => {
                const total = (config.total_hours || 50) + (config.extra_hours || 0);
                const usedPct = total > 0 ? Math.min(100, ((config.used_hours || 0) / total) * 100) : 0;
                return (
                  <div key={config.username} className="relative overflow-hidden p-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-violet-400/25 transition-all">
                    <div className="pointer-events-none absolute -top-16 right-1/4 w-48 h-32 bg-violet-500/[0.06] rounded-full blur-[70px]" />
                    <div className="relative flex items-center justify-between mb-4 flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/25 to-pink-500/15 border border-violet-400/30 flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">{(config.display_name || '?').charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{config.display_name}</p>
                          <p className="text-white/40 text-[10px] tracking-wide capitalize">{config.role}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openAddHoursModal(config)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500/[0.08] border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/[0.15] text-[11px] font-medium transition-all">
                          <Plus className="w-3 h-3" /> Hours
                        </button>
                        <button onClick={() => openConfigModal('leave', config)}
                          className="w-8 h-8 rounded-full bg-violet-500/[0.08] border border-violet-500/25 text-violet-300 hover:bg-violet-500/[0.15] flex items-center justify-center transition-all">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Stats mini grid */}
                    <div className="relative grid grid-cols-5 gap-2 mb-3">
                      {[
                        { label: 'Base',  value: `${config.total_hours || 50}h`, hex: '#FFFFFF', op: '0.6' },
                        { label: 'Bonus', value: `+${config.extra_hours || 0}h`, hex: '#10B981' },
                        { label: 'Used',  value: `${config.used_hours || 0}h`,   hex: '#00E5FF' },
                        { label: 'Left',  value: `${config.remaining_hours}h`,   hex: '#8A2BE2' },
                        { label: 'Worked', value: `${config.worked_hours || 0}h`, hex: (config.worked_hours || 0) >= (config.minimum_hours_required || 0) ? '#10B981' : '#F43F5E' },
                      ].map((s, i) => (
                        <div key={i} className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] text-center">
                          <p className="text-lg font-light tabular-nums" style={{ color: s.hex, opacity: s.op || 1 }}>{s.value}</p>
                          <p className="text-[9px] tracking-[0.15em] uppercase text-white/40 mt-1">{s.label}</p>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-full h-2 bg-white/[0.04] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-cyan-400 via-violet-500 to-pink-500 transition-all"
                        style={{ width: `${usedPct}%` }} />
                    </div>
                    <p className="text-right text-[10px] text-white/40 mt-1 font-mono">{Math.round(usedPct)}% used</p>

                    {config.adjustments && config.adjustments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/[0.05]">
                        <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mb-2">Recent adjustments</p>
                        <div className="space-y-1">
                          {config.adjustments.slice(-2).reverse().map((adj, i) => (
                            <div key={i} className="flex justify-between items-center text-[11px] rounded-lg px-3 py-1.5 bg-white/[0.02] border border-white/[0.05]">
                              <span className="text-white/60 truncate flex-1 mr-2">{adj.reason}</span>
                              <span className={`font-mono ${adj.hours > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{adj.hours > 0 ? '+' : ''}{adj.hours}h</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </JewelModal>

      {/* Config Modal (Schedule/Payday/Leave) */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {configType === 'schedule' && <><Calendar className="w-5 h-5 text-green-400" /> Configure Schedule</>}
              {configType === 'payday' && <><DollarSign className="w-5 h-5 text-cyan-400" /> Configure Payday</>}
              {configType === 'leave' && <><Briefcase className="w-5 h-5 text-purple-400" /> Configure Leave Allowance</>}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Schedule Config */}
            {configType === 'schedule' && (
              <>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">Staff Member *</label>
                  <select
                    value={scheduleForm.staff_username}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, staff_username: e.target.value })}
                    className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                    disabled={selectedStaffConfig?.username}
                  >
                    <option value="">Select staff member</option>
                    {staffList.map(staff => (
                      <option key={staff.username} value={staff.username}>{staff.display_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">Day of Week *</label>
                  <select
                    value={scheduleForm.day_of_week}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, day_of_week: parseInt(e.target.value) })}
                    className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <option key={day.id} value={day.id}>{day.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-white/50 mb-1 block">Start Time *</label>
                    <input
                      type="time"
                      value={scheduleForm.start_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                      className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/50 mb-1 block">End Time *</label>
                    <input
                      type="time"
                      value={scheduleForm.end_time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                      className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancel</Button>
                  <Button onClick={handleSaveSchedule} className="bg-green-500 hover:bg-green-600">
                    <Save className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>
              </>
            )}
            
            {/* Payday Config */}
            {configType === 'payday' && (
              <>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">Staff Member *</label>
                  <select
                    value={paydayForm.staff_username}
                    onChange={(e) => setPaydayForm({ ...paydayForm, staff_username: e.target.value })}
                    className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">Select staff member</option>
                    {staffList.map(staff => (
                      <option key={staff.username} value={staff.username}>{staff.display_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">Payday Frequency *</label>
                  <select
                    value={paydayForm.payday_type}
                    onChange={(e) => setPaydayForm({ ...paydayForm, payday_type: e.target.value, payday_day: e.target.value === 'monthly' ? 1 : 0 })}
                    className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                  >
                    {PAYDAY_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">
                    {paydayForm.payday_type === 'monthly' ? 'Day of Month *' : 'Day of Week *'}
                  </label>
                  {paydayForm.payday_type === 'monthly' ? (
                    <select
                      value={paydayForm.payday_day}
                      onChange={(e) => setPaydayForm({ ...paydayForm, payday_day: parseInt(e.target.value) })}
                      className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                    >
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={paydayForm.payday_day}
                      onChange={(e) => setPaydayForm({ ...paydayForm, payday_day: parseInt(e.target.value) })}
                      className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                    >
                      {DAYS_OF_WEEK.map(day => (
                        <option key={day.id} value={day.id}>{day.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancel</Button>
                  <Button onClick={handleSavePayday} className="bg-cyan-500 hover:bg-cyan-600 text-black">
                    <Save className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>
              </>
            )}
            
            {/* Leave Config */}
            {configType === 'leave' && (
              <>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">Staff Member *</label>
                  <select
                    value={leaveConfigForm.staff_username}
                    onChange={(e) => setLeaveConfigForm({ ...leaveConfigForm, staff_username: e.target.value })}
                    className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                    disabled={selectedStaffConfig?.username}
                  >
                    <option value="">Select staff member</option>
                    {staffList.map(staff => (
                      <option key={staff.username} value={staff.username}>{staff.display_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">Minimum Hours Required</label>
                  <input
                    type="number"
                    value={leaveConfigForm.minimum_hours}
                    onChange={(e) => setLeaveConfigForm({ ...leaveConfigForm, minimum_hours: parseFloat(e.target.value) })}
                    className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                  />
                  <p className="text-white/40 text-xs mt-1">Staff must work this many hours before requesting leave</p>
                </div>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">Total Allowance (hours/year)</label>
                  <input
                    type="number"
                    value={leaveConfigForm.total_allowance}
                    onChange={(e) => setLeaveConfigForm({ ...leaveConfigForm, total_allowance: parseFloat(e.target.value) })}
                    className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/50 mb-1 block">Extra Hours (bonus)</label>
                  <input
                    type="number"
                    value={leaveConfigForm.extra_hours}
                    onChange={(e) => setLeaveConfigForm({ ...leaveConfigForm, extra_hours: parseFloat(e.target.value) })}
                    className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancel</Button>
                  <Button onClick={handleSaveLeaveConfig} className="bg-purple-500 hover:bg-purple-600">
                    <Save className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Deduct Hours Modal */}
      <Dialog open={showAddHoursModal} onOpenChange={setShowAddHoursModal}>
        <DialogContent className="bg-gray-900 border-green-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Add or Deduct Leave Hours
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Staff Member *</label>
              <select
                value={addHoursForm.staff_username}
                onChange={(e) => setAddHoursForm({ ...addHoursForm, staff_username: e.target.value })}
                className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
              >
                <option value="">Select staff member</option>
                {staffList.map(staff => (
                  <option key={staff.username} value={staff.username}>{staff.display_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-white/50 mb-1 block">Hours to Add/Deduct *</label>
              <input
                type="number"
                value={addHoursForm.hours}
                onChange={(e) => setAddHoursForm({ ...addHoursForm, hours: parseFloat(e.target.value) || 0 })}
                className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                placeholder="Enter positive to add, negative to deduct"
              />
              <p className="text-white/40 text-xs mt-1">Use positive numbers to add hours, negative to deduct</p>
            </div>
            <div>
              <label className="text-sm text-white/50 mb-1 block">Reason *</label>
              <textarea
                value={addHoursForm.reason}
                onChange={(e) => setAddHoursForm({ ...addHoursForm, reason: e.target.value })}
                className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                rows={2}
                placeholder="e.g., Bonus for excellent performance, Overtime compensation"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowAddHoursModal(false)}>Cancel</Button>
              <Button onClick={handleAddHours} className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700">
                <Save className="w-4 h-4 mr-2" /> {addHoursForm.hours >= 0 ? 'Add' : 'Deduct'} Hours
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shift Templates Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-violet-400" /> Shift Templates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Create new */}
            {canAddShifts && (
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.08]/50 space-y-3">
                <p className="text-xs text-white/50 font-medium">Create New Template</p>
                <input value={newTemplate.name} onChange={e => setNewTemplate(p => ({ ...p, name: e.target.value }))}
                  placeholder="Template name (e.g. Morning Shift)" data-testid="template-name-input"
                  className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500" />
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-white/40 mb-1 block">Start</label>
                    <input type="time" value={newTemplate.start_time} onChange={e => setNewTemplate(p => ({ ...p, start_time: e.target.value }))}
                      className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-white/40 mb-1 block">End</label>
                    <input type="time" value={newTemplate.end_time} onChange={e => setNewTemplate(p => ({ ...p, end_time: e.target.value }))}
                      className="w-full bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
                <Button onClick={async () => {
                  if (!newTemplate.name) return;
                  const h = getAuthHeaders();
                  if (!h) return;
                  await axios.post(`${BACKEND_URL}/api/shifts/templates`, newTemplate, { headers: h });
                  setNewTemplate({ name: '', start_time: '09:00', end_time: '17:00', shift_type: 'regular', description: '' });
                  const r = await axios.get(`${BACKEND_URL}/api/shifts/templates`, { headers: h });
                  setShiftTemplates(r.data || []);
                  toast.success('Template created');
                }} className="w-full bg-violet-500 hover:bg-violet-600" data-testid="create-template-btn">Create Template</Button>
              </div>
            )}

            {/* Existing templates */}
            <div className="space-y-2">
              {shiftTemplates.length === 0 ? (
                <p className="text-white/30 text-xs text-center py-4">No templates yet</p>
              ) : shiftTemplates.map(t => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.08] group">
                  <div>
                    <p className="text-white text-sm font-medium">{t.name}</p>
                    <p className="text-white/40 text-xs">{t.start_time} - {t.end_time} · {t.shift_type}</p>
                  </div>
                  {canAddShifts && (
                    <Button size="sm" variant="ghost" className="text-red-400 opacity-0 group-hover:opacity-100"
                      onClick={async () => {
                        const h = getAuthHeaders();
                        if (!h) return;
                        await axios.delete(`${BACKEND_URL}/api/shifts/templates/${t.id}`, { headers: h });
                        const r = await axios.get(`${BACKEND_URL}/api/shifts/templates`, { headers: h });
                        setShiftTemplates(r.data || []);
                        toast.success('Deleted');
                      }}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveManagementPage;
